// Seattle, "Drizzle over the Needle". A November evening of soft drizzle, just after dusk, from Kerry Park on the south
// slope of Queen Anne Hill. We stand at the back of the park's terrace, near its western end, about 100 m above the
// bay, and look south-south-east (about 142 degrees) over the railing: the Space Needle, 1.3 km off and nearer than
// anything else, its saucer lit above the downtown skyline; Columbia Center tall and dark just right of it, the
// pyramid-capped tower and the waterfront towers stepping down to the right; Two Union Square, the twin round hotel
// towers and the sloped glass tower to the left; Capitol Hill's TV masts at the far left. Right of downtown the land
// drops to Elliott Bay: the stadium arches, the Great Wheel on its pier, the orange port cranes of Terminal 46 and
// Harbor Island, West Seattle's dark ridge, a bulk carrier at anchor, and the white-and-green state ferry crossing.
// Mount Rainier stands behind the port, hidden in cloud until a gap opens for a minute. Below the wall Queen Anne's
// wooden houses, a brick apartment block with a rooftop sign and tall firs step down the hill.
// The horizontal angles are compressed toward the right (the cranes are really 30 degrees right of the Needle); the
// order of everything is as it is seen. Not in view from here: Pike Place Market and its sign (behind Belltown's
// towers), the Fremont Troll (north, behind us), Lake Union (east, out of frame), the monorail (hidden in the trees).
//
// The loop's timeline (seconds; everything else runs all loop long):
//   event                               time            period
//   crows fly to their roost            6-30, 126-150   two passes
//   ferry leaves for the island, horn   20-92           once (horn at 21)
//   buoy bell                           46, 133, 206    three
//   seaplane lets down to Lake Union    58-86           once
//   the sign's M stutters               64, 187         two
//   a gull glides past                  98-108, 196-206 two (calls at 100, 198)
//   the cloud opens on Mount Rainier    96-152          once (clear 112-136)
//   trolleybus down / up the hill       116-130, 224-237
//   the park lamp dips                  142             once
//   ferry comes in from the island      150-222         once (horn at 151)
//   an airliner sinks toward Sea-Tac    166-214         once
//   cars up the hill / down the hill    every 40 s / every 60 s
//   the Needle's lifts                  every 40 s and every 60 s (up and down)
//   the Great Wheel changes colour      every 30 s
//   dog walker, jogger, walker          34-86 and 154-206; 12, 92, 172 (16 s each); 62-108 and 182-228
//   thermos poured, coffee sipped       every 40 s; every 24 s
//   the crow shifts along the rail      every 15 s
// The longest stretch with nothing timed starting or ending is about 9 s (.tmp/seattle/count.mjs).
import { W, H, LOOP, TAU, frac, clamp, lerp, smooth, wave, step, env, cycle,
  hash, rng, bayer, hex, makeCanvas, px, rect, makeHalo, drawHalo, text, reflect, streak, makePitch } from '../engine/core.js';
import { weatherPlans, drawWeather } from '../engine/weather.js';

let ctx;                                             // this scene's 480x270 context, set by create()
const halo = (hl, x, y, a = 1) => drawHalo(ctx, hl, x, y, a);

/* ================= Scene constants ================= */
const HOR = 138;                                     // eye level: the park stands about 100 m above the bay
const ND = { cx: 168, tip: 22, waist: 88, base: 196 };   // the Space Needle: axis, tip of the spire, waist, foot
const SHORE = 156;                                   // Elliott Bay's far shore (Harbor Island, West Seattle)
const RAIL = 206, WALL = 210, FOOT = 218;            // the park's railing, the top of its low wall, the wall's foot
const NEAR_BOT = WALL + 2;                           // the hill's houses are drawn down to here (the wall hides the rest)
const BILL = { x: 99, y: 127, w: 46, h: 18 };        // the rooftop sign's face
const APT = { x0: 92, x1: 152, roof: 150 };          // the brick apartment block it stands on
const MT = { x: 272, y: 97, w: 126, h: 40 };         // Mount Rainier's box
const WHEEL = { cx: 321, cy: 168, r: 9 };            // the Great Wheel on its pier
const LAMP = { x: 60, y: 182, base: 247 };           // the park lamp (its globe, the foot of its post)
const BENCH = { x0: 304, x1: 346, seat: 237 };       // a bench facing the view
const FORM = { x: 398, top: 193, base: 246 };        // "Changing Form", the park's steel sculpture
const STREET = { x0: 281, y0: 164, x1: 272, y1: 210 };   // a steep street dropping to the bay (far end, near end)
const BUOY = { x: 354, y: 184 };                     // a bell buoy
const SHIP = { x: 388, y: 179 };                     // a bulk carrier at anchor (its bow, its waterline)

/* ================= Colour + pixel helpers ================= */
const RGB = {};
const rgbOf = h => RGB[h] || (RGB[h] = hex(h));
const hx2 = v => (v < 16 ? '0' : '') + v.toString(16);
/** #rrggbb between two #rrggbb colours */
function mixHex(a, b, t) {
  const A = rgbOf(a), B = rgbOf(b);
  return '#' + hx2(Math.round(lerp(A[0], B[0], t))) + hx2(Math.round(lerp(A[1], B[1], t))) + hx2(Math.round(lerp(A[2], B[2], t)));
}
/** ordered-dither pick from a palette (v in 0..1) */
function dpick(pal, v, i, y) {
  const f = clamp(v, 0, 1) * (pal.length - 1);
  let k = Math.floor(f);
  if (f - k > bayer(i, y)) k++;
  return pal[Math.min(k, pal.length - 1)];
}
/** ImageData-backed painter for prerendering (hex colours) */
function painter(w = W, h = H) {
  const [c, x] = makeCanvas(w, h);
  const img = x.createImageData(w, h), d = img.data;
  const p = { c, x, w, h };
  p.set = (i, y, col) => {
    if (i < 0 || y < 0 || i >= w || y >= h) return;
    const k = rgbOf(col), o = (y * w + i) * 4;
    d[o] = k[0]; d[o + 1] = k[1]; d[o + 2] = k[2]; d[o + 3] = 255;
  };
  p.seta = (i, y, col, a) => {                       // with alpha (0..1)
    if (i < 0 || y < 0 || i >= w || y >= h) return;
    const k = rgbOf(col), o = (y * w + i) * 4;
    d[o] = k[0]; d[o + 1] = k[1]; d[o + 2] = k[2]; d[o + 3] = Math.round(a * 255);
  };
  p.clear = (i, y) => { if (i >= 0 && y >= 0 && i < w && y < h) d[(y * w + i) * 4 + 3] = 0; };
  p.has = (i, y) => i >= 0 && y >= 0 && i < w && y < h && d[(y * w + i) * 4 + 3] > 0;
  p.rect = (x0, y0, ww, hh, col) => { for (let y = y0; y < y0 + hh; y++) for (let i = x0; i < x0 + ww; i++) p.set(i, y, col); };
  p.line = (x0, y0, x1, y1, col) => {
    const n = Math.max(Math.abs(x1 - x0), Math.abs(y1 - y0), 1);
    for (let k = 0; k <= n; k++) p.set(Math.round(lerp(x0, x1, k / n)), Math.round(lerp(y0, y1, k / n)), col);
  };
  p.done = () => { x.putImageData(img, 0, 0); return c; };
  return p;
}
/** sprite from rows of characters and a colour key ('.' or unknown = transparent) */
function sprite(rows, key, flip = false) {
  const h = rows.length, w = Math.max(...rows.map(r => r.length));
  const p = painter(w, h);
  rows.forEach((row, y) => { for (let i = 0; i < row.length; i++) { const col = key[row[i]]; if (col) p.set(flip ? w - 1 - i : i, y, col); } });
  return p.done();
}
/** a ridge through control points [[x, y], ...], straight between them */
function ridge(pts, x) {
  if (x <= pts[0][0]) return pts[0][1];
  for (let k = 0; k < pts.length - 1; k++) {
    const [xa, ya] = pts[k], [xb, yb] = pts[k + 1];
    if (x <= xb) return ya + (yb - ya) * (x - xa) / (xb - xa);
  }
  return pts[pts.length - 1][1];
}
/** crop a full-frame layer to its painted bounding box, so each frame blends only the pixels it needs */
function trim(c) {
  const w = c.width, h = c.height, d = c.getContext('2d').getImageData(0, 0, w, h).data;
  let x0 = w, y0 = h, x1 = -1, y1 = -1;
  for (let y = 0; y < h; y++) for (let i = 0; i < w; i++) if (d[(y * w + i) * 4 + 3]) {
    if (i < x0) x0 = i; if (i > x1) x1 = i; if (y < y0) y0 = y; if (y > y1) y1 = y;
  }
  if (x1 < 0) return { c, x: 0, y: 0 };
  const [t, tx] = makeCanvas(x1 - x0 + 1, y1 - y0 + 1);
  tx.drawImage(c, x0, y0, t.width, t.height, 0, 0, t.width, t.height);
  return { c: t, x: x0, y: y0 };
}
const blit = (L, a = 1) => { ctx.globalAlpha = a; ctx.drawImage(L.c, L.x, L.y); ctx.globalAlpha = 1; };
const R = (x, y, w, h, c) => { ctx.fillStyle = c; ctx.fillRect(x, y, w, h); };
/** a window that switches on and off n times a loop */
const winOn = (w, salt, t) => hash(w.id + salt, Math.floor(frac(t / LOOP + w.off) * w.n), 3) < w.p;
function flick(t, list, id) {
  let v = 1;
  for (const [a, b] of list) if (t >= a && t < b) { const hv = hash(id, Math.floor(t * 20)); v = hv > 0.45 ? 1 : hv > 0.2 ? 0.4 : 0.1; }
  return v;
}

/* ================= A light south-westerly: the drizzle's slant, the smoke, the firs ================= */
const breeze = t => clamp(0.4 + 0.2 * wave(t, 3, 0.2) + 0.1 * wave(t, 8, 0.6), 0, 1);

/* ================= Sky: the blue hour under a low deck, rose over downtown, the last green light in the west ================= */
const SKY = ['#070b15', '#0a101d', '#0d1626', '#111c2f', '#152338', '#1a2b41', '#203349', '#273b50', '#2f4356', '#384a5b'].map(hex);
const ROSE = ['#2c2a3e', '#3c3348', '#4c3d50', '#5e4955', '#70555a', '#82625e'].map(hex);   // the city's glow on the cloud base
const TEAL = ['#2a4654', '#2e525a', '#345f60', '#3c6e66', '#487e6e'].map(hex);              // the afterglow over the Sound
function renderSky() {
  const [c, x] = makeCanvas(W, H);
  const img = x.createImageData(W, H), d = img.data;
  const pick = (pal, v, i, y) => { const f = clamp(v, 0, 1) * (pal.length - 1); let k = Math.floor(f); if (f - k > bayer(i, y)) k++; return pal[Math.min(k, pal.length - 1)]; };
  for (let y = 0; y < H; y++) for (let i = 0; i < W; i++) {
    const v = Math.pow(Math.min(1, y / 150), 1.3) * 0.9 + 0.03 * Math.sin(i * 0.027 + y * 0.11) * Math.sin(i * 0.011 - y * 0.05);
    const g1 = Math.max(0, 1 - Math.hypot((i - 196) / 240, (y - 152) / 100));     // downtown's glow
    const g2 = Math.max(0, 1 - Math.hypot((i - 500) / 220, (y - 146) / 64));      // dusk in the west
    let col = pick(SKY, v, i, y);
    if (Math.pow(g2, 1.3) * 1.5 > bayer(i + 1, y + 2)) col = pick(TEAL, g2 * 1.3 - 0.2, i, y);
    if (g1 * 1.05 > bayer(i + 2, y + 1)) col = pick(ROSE, g1 * 1.25 - 0.2, i, y);
    const o = (y * W + i) * 4;
    d[o] = col[0]; d[o + 1] = col[1]; d[o + 2] = col[2]; d[o + 3] = 255;
  }
  x.putImageData(img, 0, 0);
  return c;
}

/* ================= The cloud deck (three tileable layers, drifting in from the Sound; bellies lit by the city) ================= */
const CLOUD_DEFS = [
  { seed: 13, y: -10, h: 58, banks: 5, k: 1, pal: { top: '#1c2231', body: '#262d3e', core: '#20273a', bot: '#3c3a4a', bot2: '#2e3142' } },
  { seed: 29, y: 20, h: 60, banks: 5, k: 2, pal: { top: '#2a3042', body: '#343b4e', core: '#2c3346', bot: '#5a4c58', bot2: '#44404e' } },
  { seed: 47, y: 56, h: 54, banks: 4, k: 3, pal: { top: '#2a3042', body: '#30364a', core: '#282e40', bot: '#8a6c70', bot2: '#5e5260' } },
  { seed: 61, y: 92, h: 46, banks: 4, k: 1, flat: true, pal: { top: '#343a4e', body: '#2a3044', core: '#242a3c', bot: '#7a626a', bot2: '#4e4858' } },   // bars of stratus low over the Sound
];
function makeCloudLayer(def) {
  const r = rng(def.seed), h = def.h, puffs = [];
  for (let b = 0; b < def.banks; b++) {
    const bx = (b + r() * 0.8) * W / def.banks, bw = 70 + r() * 100, base = h * (0.3 + r() * 0.4);
    const np = 8 + Math.floor(r() * 7);
    for (let i = 0; i < np; i++) {
      const rx = (15 + r() * 28) * (def.flat ? 1.5 : 1), ry = (3 + r() * 5.5) * (def.flat ? 0.5 : 1);   // flat, soft stratus
      const pb = base + (r() - 0.5) * 8;
      puffs.push({ x: bx + (r() - 0.5) * bw, y: pb - r() * ry * 1.5, rx, ry, base: pb + ry * 0.3 });
    }
  }
  const D = new Float32Array(W * h);
  for (let y = 0; y < h; y++) for (let x = 0; x < W; x++) {
    let best = -9;
    for (const p of puffs) {
      let dx = x - p.x; dx -= Math.round(dx / W) * W;
      const dy = y - p.y;
      let v = 1 - (dx * dx) / (p.rx * p.rx) - (dy * dy) / (p.ry * p.ry);
      if (y > p.base) v -= (y - p.base) * 0.22;
      if (v > best) best = v;
    }
    best += 0.13 * Math.sin(TAU * 9 * x / W + y * 0.7 + def.seed) * Math.sin(TAU * 5 * x / W - y * 0.3);
    best -= Math.max(0, (y - (h - 12)) / 12) * 1.6;                   // nothing reaches the layer's bottom edge
    D[y * W + x] = best;
  }
  const get = (x, y) => (y < 0 || y >= h) ? -9 : D[y * W + ((x % W) + W) % W];
  const [c, cx] = makeCanvas(W, h);
  const img = cx.createImageData(W, h), d = img.data, P = {};
  for (const k in def.pal) P[k] = hex(def.pal[k]);
  for (let y = 0; y < h; y++) for (let x = 0; x < W; x++) {
    const th = (bayer(x, y) - 0.5) * 0.3, v = D[y * W + x];
    if (v + th <= 0.03) continue;
    let col;
    if (get(x, y - 1) + th <= 0.03 || get(x, y - 2) <= 0) col = P.top;
    else if (get(x, y + 1) + th <= 0.03) col = P.bot;
    else if (get(x, y + 3) <= 0.05 + bayer(x, y) * 0.2) col = P.bot2;
    else if (v > 0.55 + th) col = P.core;
    else col = P.body;
    const o = (y * W + x) * 4;
    d[o] = col[0]; d[o + 1] = col[1]; d[o + 2] = col[2]; d[o + 3] = 255;
  }
  cx.putImageData(img, 0, 0);
  return { c, y: def.y, k: def.k };
}
/* low cloud and mist (tileable, four dithered levels of alpha): through the tower tops, over the bay, across the firs */
function renderWisp(seed, h, dens, colA, colB, n = 5) {
  const p = painter(W, h), r = rng(seed), blobs = [];
  for (let i = 0; i < n; i++) blobs.push({ x: r() * W, y: h * (0.4 + r() * 0.25), rx: 26 + r() * 54, ry: 2.5 + r() * h * 0.2 });
  for (let y = 0; y < h; y++) for (let i = 0; i < W; i++) {
    let v = 0;
    for (const b of blobs) {
      let dx = i - b.x; dx -= Math.round(dx / W) * W;
      v = Math.max(v, 1 - (dx * dx) / (b.rx * b.rx) - ((y - b.y) ** 2) / (b.ry * b.ry));
    }
    v += 0.22 * Math.sin(TAU * 7 * i / W + y * 0.6 + seed) * Math.sin(TAU * 13 * i / W - y * 0.25) - 0.08;
    if (v <= 0) continue;
    const q = Math.floor(Math.min(1, v * 1.3) * 4 + bayer(i, y)) / 4;
    if (q > 0) p.seta(i, y, v > 0.5 ? colA : colB, q * dens);
  }
  return p.done();
}

/* ================= Mist banks: ordered-dithered, drifting, their density breathing =================
   Each bank is pre-rendered at FOG_LEVELS+1 densities (a 4-step dithered alpha mask); per frame the two levels around
   the current density are crossfaded, so the mist thickens and thins smoothly while it drifts. */
const FOG_DEFS = [
  { seed: 5, y: 102, h: 66, k: 1, sp: 0.25, top: '#6c7288', bot: '#9c8a92', alpha: 0.64,                     // between downtown and the Needle, over the bay
    dens: t => 0.52 + 0.2 * wave(t, 3, 0.1) + 0.08 * wave(t, 7, 0.6) },
  { seed: 8, y: 152, h: 62, k: 2, sp: 0.7, top: '#5e6a82', bot: '#78829a', alpha: 0.5,                    // across the firs and roofs below the wall
    dens: t => 0.5 + 0.22 * wave(t, 4, 0.35) + 0.08 * wave(t, 9, 0.2) },
];
const FOG_LEVELS = 8;
function makeFog(def) {
  const h = def.h, n = W * h, F = new Float32Array(n), A = hex(def.top), B = hex(def.bot);
  for (let y = 0; y < h; y++) for (let x = 0; x < W; x++) {
    const v = y / (h - 1), prof = Math.pow(Math.sin(Math.PI * Math.pow(v, 0.8)), 1.6);
    const nz = 0.48 + 0.34 * Math.sin(TAU * 2 * x / W + def.seed + y * 0.05) + 0.16 * Math.sin(TAU * 5 * x / W - y * 0.19 + def.seed * 2)
      + 0.1 * Math.sin(TAU * 11 * x / W + y * 0.41) + 0.06 * Math.sin(TAU * 23 * x / W - y * 0.7);
    F[y * W + x] = prof * clamp(nz, 0, 1);
  }
  const levels = [];
  for (let k = 0; k <= FOG_LEVELS; k++) {
    const D = k / FOG_LEVELS * 1.25;
    const [c, cx] = makeCanvas(W, h), img = cx.createImageData(W, h), d = img.data;
    for (let p = 0; p < n; p++) {
      const x = p % W, y = (p / W) | 0;
      const q = Math.floor(Math.min(1, F[p] * D) * 4 + bayer(x, y)) / 4;
      if (q <= 0) continue;
      const m = y / (h - 1), o = p * 4;
      d[o] = A[0] + (B[0] - A[0]) * m; d[o + 1] = A[1] + (B[1] - A[1]) * m; d[o + 2] = A[2] + (B[2] - A[2]) * m;
      d[o + 3] = Math.round(Math.min(1, q) * 255);
    }
    cx.putImageData(img, 0, 0);
    levels.push(c);
  }
  return { ...def, levels };
}

/* ================= Mount Rainier: 98 km off, a pale shape through a gap in the cloud, never crisp =================
   From the north-north-west: Little Tahoma's point on the left shoulder, the broad summit dome with Liberty Cap toward
   the right, the dark Willis Wall under it, the long glaciers. The last light comes from the right. */
const MT_RIDGE = [[0, 39], [14, 35], [26, 30], [33, 26], [36, 21], [40, 25], [48, 17], [55, 9], [61, 4], [66, 2], [72, 2], [77, 1],
  [82, 4], [90, 11], [100, 20], [111, 28], [121, 34], [126, 39]];
const MT_PAL = ['#56628a', '#6a769c', '#808cae', '#98a2c0', '#b2b8d0'];
function renderRainier() {
  const p = painter(MT.w, MT.h);
  for (let x = 0; x < MT.w; x++) {
    const top = Math.round(ridge(MT_RIDGE, x) + (hash(x, 0, 91) - 0.5) * 0.9);
    for (let y = top; y < MT.h; y++) {
      const d = y - top, u = x / MT.w;
      let v = 0.42 + 0.3 * (u - 0.5) + 0.25 * Math.exp(-d / 5) + (hash(x >> 1, y >> 1, 92) - 0.5) * 0.22;
      const rib = Math.sin(x * 0.55 + y * 0.35) * Math.sin(x * 0.17 - y * 0.2);   // rock ribs between the glaciers
      if (rib > 0.45 && d > 3) v -= 0.3;
      if (x > 60 && x < 82 && d > 5 && d < 16 && (x + y) % 3 !== 0) v -= 0.28;      // the Willis Wall
      const fade = 1 - smooth(14, 36, y) * 0.95;                                    // sinking into the haze
      if (fade <= bayer(x, y) * 0.9) continue;
      p.seta(x, y, dpick(MT_PAL, v, x, y), 1);
    }
  }
  return p.done();
}
/* the bank of low cloud that hides it most of the evening: part of the deck, its belly rose toward the city */
const VEIL = { w: 204, h: 58 };
function renderVeil() {
  const { w, h } = VEIL, p = painter(w, h), r = rng(77), blobs = [];
  for (let i = 0; i < 13; i++) blobs.push({ x: 18 + r() * (w - 36), y: 15 + r() * 24, rx: 22 + r() * 40, ry: 5 + r() * 8 });
  const f = (i, y) => {
    let v = 0;
    for (const b of blobs) v = Math.max(v, 1 - ((i - b.x) ** 2) / (b.rx * b.rx) - ((y - b.y) ** 2) / (b.ry * b.ry));
    v += 0.14 * Math.sin(i * 0.21 + y * 0.5) * Math.sin(i * 0.09 - y * 0.3);
    return v * smooth(0, 18, i) * (1 - smooth(w - 18, w, i));
  };
  for (let y = 0; y < h; y++) for (let i = 0; i < w; i++) {
    const v = f(i, y);
    if (v <= 0.04) continue;
    const q = Math.floor(Math.min(1, v * 1.7) * 4 + bayer(i, y)) / 4;
    if (q <= 0) continue;
    const col = f(i, y - 2) <= 0.04 ? '#3a4052' : f(i, y + 2) <= 0.04 ? (i < 96 ? '#74606a' : '#5c6c72') : f(i, y + 5) <= 0.06 + bayer(i, y) * 0.2 ? (i < 96 ? '#585060' : '#4c5a64') : v > 0.6 ? '#3a4054' : '#444a5c';
    p.seta(i, y, col, q);
  }
  return p.done();
}
/* the gap itself: a patch of the clear sky with soft, dithered edges, laid over the cloud layers as it opens */
const GAP_BOX = { x: MT.x - 25, y: MT.y - 15, w: 176, h: 62 };
function renderGap(sky) {
  const { w, h } = GAP_BOX, [c, x] = makeCanvas(w, h);
  const src = sky.getContext('2d').getImageData(GAP_BOX.x, GAP_BOX.y, w, h).data, img = x.createImageData(w, h), d = img.data;
  for (let y = 0; y < h; y++) for (let i = 0; i < w; i++) {
    const dd = Math.hypot((i - w / 2) / (w / 2), (y - h * 0.46) / (h / 2)) + 0.1 * Math.sin(i * 0.19) * Math.sin(y * 0.4 + 1);
    const q = Math.floor((1 - smooth(0.5, 1, dd)) * 4 + bayer(i, y)) / 4;
    if (q <= 0) continue;
    const o = (y * w + i) * 4;
    d[o] = src[o]; d[o + 1] = src[o + 1]; d[o + 2] = src[o + 2]; d[o + 3] = Math.round(Math.min(1, q) * 255);
  }
  x.putImageData(img, 0, 0);
  return c;
}
/* the drizzle's haze between the park and downtown (dithered, drawn with alpha): it sets the towers back */
function renderHaze() {
  const p = painter();
  for (let y = 92; y < 162; y++) for (let i = 0; i < 312; i++) {
    const v = smooth(96, 158, y) * 0.85 * (1 - smooth(282, 312, i));
    if (v > bayer(i, y) * 0.95) p.set(i, y, y > 140 ? '#5a5a6e' : '#4e566c');
  }
  return p.done();
}

/* ================= The far shore: Capitol Hill and its TV masts (left); the stadiums, Terminal 46, Harbor Island's
   cranes and West Seattle's ridge across the bay (right) ================= */
const MASTS = [{ x: 11, top: 86 }, { x: 27, top: 94 }];
const CH_R = [[0, 124], [24, 121], [48, 126], [72, 134], [100, 143]];
const WS_R = [[340, 157], [358, 152], [384, 144], [410, 137], [438, 133], [462, 134], [480, 136]];
//               x (waterside leg), foot, height, boom raised, the side its boom is on
const CRANES = [{ x: 352, y: 155, h: 19, up: true, d: 1 }, { x: 363, y: 155, h: 19, up: false, d: 1 }, { x: 374, y: 155, h: 19, up: true, d: 1 },
  { x: 409, y: 154, h: 13, up: true, d: -1 }, { x: 418, y: 154, h: 13, up: true, d: -1 }, { x: 427, y: 154, h: 13, up: false, d: -1 },
  { x: 441, y: 154, h: 12, up: true, d: -1 }, { x: 450, y: 154, h: 12, up: true, d: -1 }];
const CRANE_TOP = c => c.y - Math.round(c.h * 0.72);
const WARM_A = '#ffd88e', WARM_B = '#eea45e', COOL_A = '#d4ecff', COOL_B = '#9cc8ec';
function renderFar() {
  const p = painter(), r = rng(17), twinkle = [];
  // Capitol Hill: a low ridge of flats and trees behind South Lake Union
  for (let x = 0; x < 104; x++) {
    const top = Math.round(ridge(CH_R, x) + (hash(x >> 1, 0, 11) < 0.3 ? -1 : 0));
    for (let y = top; y < 152; y++) p.set(x, y, y === top ? '#232c3c' : dpick(['#121824', '#161d2b', '#1b2333'], 0.3 + (y - top) / 40 + (hash(x >> 1, y >> 1, 12) - 0.5) * 0.5, x, y));
  }
  for (let n = 0; n < 130; n++) {
    const x = Math.floor(r() * 100), y = Math.round(ridge(CH_R, x)) + 3 + Math.floor(r() * 22);
    if (y > 150) continue;
    const col = r() < 0.7 ? WARM_B : r() < 0.5 ? WARM_A : COOL_B;
    if (r() < 0.25) twinkle.push({ x, y, col, id: twinkle.length }); else p.set(x, y, r() < 0.5 ? mixHex(col, '#1b2333', 0.45) : col);
  }
  for (const m of MASTS) {                                             // lattice masts, guyed, red lamps (lit per frame)
    for (let y = m.top; y < Math.round(ridge(CH_R, m.x)) + 1; y++) { p.set(m.x, y, (y & 1) ? '#3a4458' : '#2c3548'); if ((y - m.top) % 6 === 5 && y > m.top + 8) { p.set(m.x - 1, y, '#2c3548'); p.set(m.x + 1, y, '#2c3548'); } }
    p.set(m.x - 1, m.top + 4, '#3a4458'); p.set(m.x + 1, m.top + 4, '#3a4458');
  }
  // West Seattle: the dark ridge behind the port, houses climbing its slope
  for (let x = 340; x < W; x++) {
    const top = Math.round(ridge(WS_R, x) + (hash(x >> 1, 0, 13) < 0.35 ? -1 : 0) + (hash(x, 0, 14) < 0.2 ? 1 : 0));
    for (let y = top; y < SHORE; y++) p.set(x, y, y === top ? '#1c2a34' : dpick(['#0b1219', '#0e161e', '#121c25'], 0.2 + (y - top) / 26 + (hash(x >> 1, y >> 1, 15) - 0.5) * 0.5, x, y));
  }
  for (let n = 0; n < 190; n++) {
    const x = 346 + Math.floor(r() * 134), top = Math.round(ridge(WS_R, x)), y = top + 2 + Math.floor(Math.pow(r(), 0.8) * (SHORE - top - 3));
    if (y >= SHORE - 1) continue;
    const col = r() < 0.75 ? WARM_B : r() < 0.6 ? WARM_A : COOL_B;
    if (r() < 0.25) twinkle.push({ x, y, col, id: twinkle.length }); else p.set(x, y, r() < 0.55 ? mixHex(col, '#121c25', 0.5) : col);
  }
  // the stadiums south of downtown: two white roof arches, then the ballpark's stepped rolling roof
  for (let y = 150; y < SHORE; y++) for (let x = 286; x < 342; x++) p.set(x, y, (x + y) % 5 === 0 ? '#323a4a' : '#232a38');
  for (let k = 0; k <= 24; k++) {
    const u = k / 24, ay = Math.round(151.5 - 6.5 * Math.sin(Math.PI * u));
    p.set(288 + k, ay, '#d6dceb'); p.set(291 + k, ay + 2, '#9aa4ba');
    if (k % 6 === 3) { p.set(288 + k, ay + 1, '#6a7388'); p.set(288 + k, ay + 2, '#6a7388'); }
  }
  [[317, 147], [325, 149], [333, 151]].forEach(([x0, y0]) => {
    for (let x = x0; x < x0 + 9; x++) { p.set(x, y0, '#8e98ac'); for (let y = y0 + 1; y < SHORE; y++) p.set(x, y, (x + y) % 3 === 0 ? '#465064' : '#333c4e'); }
  });
  for (const lx of [296, 304, 322, 336]) p.set(lx, 154, '#fff0c8');
  // the port: stacks of containers and the gantry cranes, their booms up when no ship is alongside
  for (let x = 344; x < 462; x++) {
    p.set(x, SHORE - 1, x % 3 ? '#1a212c' : '#2a3340');
    if (hash(x >> 2, 0, 16) < 0.62 && (x < 392 || x > 400)) {
      const hgt = 1 + Math.floor(hash(x >> 2, 1, 16) * 3), col = ['#6a3a2c', '#2c4a5c', '#5c5a3a', '#7a4428', '#34503e'][Math.floor(hash(x >> 2, 2, 16) * 5)];
      for (let y = SHORE - 1 - hgt; y < SHORE - 1; y++) p.set(x, y, (x & 3) === 3 ? '#151b25' : col);
    }
  }
  const OR = '#d0642a', ORL = '#f49246', ORD = '#7a3818';
  for (const c of CRANES) {
    const top = CRANE_TOP(c), ax = c.x - c.d * 4, apex = c.y - c.h;
    for (let y = top; y < c.y; y++) { p.set(c.x, y, y < top + 6 ? ORL : OR); p.set(ax, y, ORD); }
    for (let x = Math.min(c.x, ax) - 1; x <= Math.max(c.x, ax) + 1; x++) { p.set(x, top, ORL); p.set(x, top + Math.round(c.h * 0.33), ORD); }
    p.line(c.x, top, c.x - c.d * 2, apex, OR); p.line(ax, top, c.x - c.d * 2, apex, ORD);     // the A-frame
    p.rect(Math.min(c.x, ax) + 1, top - 2, 2, 2, '#8a8e9a');                                   // the machinery house
    if (c.up) p.line(c.x + c.d, top - 1, c.x + c.d * 3, c.y - Math.round(c.h * 1.5), ORL);     // boom raised
    else { p.line(c.x + c.d, top, c.x + c.d * Math.round(c.h * 0.6), top, ORL); p.line(c.x - c.d * 2, apex, c.x + c.d * Math.round(c.h * 0.5), top - 1, ORD); }
    p.set(c.x, top - 1, '#fff2d0'); p.set(ax, top - 1, '#ffe0a8');
  }
  for (let x = 344; x < W; x += 3) if (r() < 0.6) p.set(x, SHORE - 1, r() < 0.5 ? '#f2c078' : '#fff0d0');   // the lights along the shore
  return { c: p.done(), twinkle };
}

/* ================= Downtown, 3 km off behind the Needle =================
   Left to right as seen from the park: South Lake Union's glass, Two Union Square's bowed crown, the twin round
   hotel towers, the gabled bank tower, the sloped glass tower; (the Needle); the vaulted municipal tower, Columbia
   Center dark and stepped, the faceted glass tower, the pyramid-capped tower, the flat-topped waterfront towers. */
//               x0   x1  top  kind
const TOWERS = [[18, 30, 110, 'glass'], [32, 46, 98, 'glass'], [47, 57, 114, 'box'], [58, 71, 92, 'glass'], [72, 85, 100, 'box'],
  [86, 102, 72, 'union'], [104, 113, 102, 'round'], [114, 123, 96, 'round'], [124, 135, 84, 'gable'], [135, 149, 70, 'slope'],
  [150, 163, 122, 'box'], [164, 178, 128, 'glass'],
  [179, 189, 80, 'vault'], [190, 208, 64, 'columbia'], [209, 218, 88, 'facet'], [219, 225, 110, 'box'],
  [226, 240, 74, 'pyramid'], [241, 252, 104, 'box'], [253, 268, 96, 'glass'], [269, 280, 116, 'box'], [281, 291, 126, 'glass']];
const GLASS = ['#111a29', '#162131', '#1c2a3b', '#243447', '#2d4054', '#394e64'];
const CONC = ['#1c1f2a', '#262935', '#313441', '#3e414e', '#4d505c'];
const STONE = ['#2c2a30', '#3c3838', '#504844', '#685c52', '#847462', '#a69072'];
const DARKG = ['#080b12', '#0b0f18', '#0f141f', '#141a27', '#1a2231'];
const BEACONS = [];                                  // red aircraft lamps on the roofs (they pulse together)
/** the top row of a tower at column i, by kind (null: no tower there) */
function towerTop(kind, x0, x1, top, i) {
  const w = x1 - x0, u = (i - x0) / w, mid = (x0 + x1) / 2, dx = i - mid;
  switch (kind) {
    case 'union': return top + Math.round(9 * Math.pow(Math.abs(u - 0.42) / 0.58, 2));
    case 'gable': return top + Math.round(Math.abs(dx) * 1.3);
    case 'slope': return top + Math.round(Math.pow(Math.max(0, x1 - 2 - i), 1.55) * 0.95);
    case 'vault': { const hw = w / 2; return top + Math.round(hw - Math.sqrt(Math.max(0, hw * hw - dx * dx))); }
    case 'columbia': return i < x0 + 2 ? top + 32 : i > x1 - 2 ? top + 14 : i < x0 + 5 ? top + 14 : i > x1 - 6 ? top + 5 : top;
    case 'pyramid': { const a = Math.abs(dx); return a <= 4 ? top + Math.round(a * 0.9) : a <= 5 ? top + 5 : a <= 6 ? top + 13 : top + 23; }
    case 'facet': return top + Math.round((1 - u) * 5);
    case 'round': return top + (i === x0 || i === x1 ? 1 : 0);
    default: return top;
  }
}
function renderDowntown() {
  const p = painter(), r = rng(66), wins = [], BOT = 156;
  for (const [x0, x1, top, kind] of TOWERS) {
    const w = x1 - x0;
    for (let i = x0; i <= x1; i++) {
      const t0 = towerTop(kind, x0, x1, top, i), u = (i - x0) / w;
      for (let y = t0; y < BOT; y++) {
        const d = y - t0;
        let col;
        if (kind === 'columbia') {                                    // black glass: only its edges catch the sky
          col = dpick(DARKG, 0.5 - 0.4 * u + (d < 2 ? 0.5 : 0) + (towerTop(kind, x0, x1, top, i - 1) > y ? 0.45 : 0), i, y);
        } else if (kind === 'box') {
          col = dpick(CONC, 0.7 - 0.5 * u - (d % 4 === 3 ? 0.12 : 0), i, y); if (d === 0) col = CONC[4];
        } else if (kind === 'union' || kind === 'gable' || kind === 'pyramid') {   // stone, crowns floodlit
          let v = 0.55 - 0.4 * u + 0.4 * Math.max(0, 1 - (y - top) / (kind === 'pyramid' ? 24 : 16));
          if (kind === 'union' && (i - x0) % 3 === 1) v -= 0.3;       // its bands of dark glass
          if (kind === 'pyramid' && y > top + 6 && y < top + 12 && (i & 1)) v = 1.2;   // the lit arcade under the cap
          col = dpick(STONE, v, i, y); if (d === 0) col = STONE[5];
        } else if (kind === 'round') {                                // cylinders: lit across the middle
          col = dpick(CONC, 0.25 + 0.6 * Math.sin(Math.PI * Math.min(1, u * 1.15)) - (d % 3 === 2 ? 0.15 : 0), i, y);
          if (d < 2) col = d ? CONC[1] : CONC[4];
        } else if (kind === 'vault') {
          col = dpick(STONE, 0.42 - 0.3 * u + (d < 2 ? 0.3 : 0) - ((i - x0) % 3 === 2 ? 0.14 : 0), i, y);
        } else {                                                      // glass: floor lines, a lighter head
          let v = 0.5 - 0.34 * u + 0.25 * Math.max(0, 1 - d / 46);
          if (d % 3 === 0) v -= 0.16;
          if (kind === 'facet' && (i - x0) + d * 0.35 < w * 0.9 && d < 22) v += 0.3;
          if (kind === 'slope') v += 0.15 + (d < 2 ? 0.35 : 0);
          col = dpick(GLASS, v, i, y); if (d === 0) col = '#4c5e76';
        }
        p.set(i, y, col);
      }
    }
    // lit offices: a fixed scatter and some that switch during the loop
    const dense = kind === 'columbia' ? 0.07 : kind === 'round' ? 0.5 : kind === 'box' ? 0.3 : 0.24, dy = kind === 'round' ? 2 : 3;
    for (let i = x0 + 1; i < x1; i += 2) for (let y = towerTop(kind, x0, x1, top, i) + 3; y < BOT - 2; y += dy) {
      if (kind === 'pyramid' && y < top + 14) continue;
      const q = r(), cool = kind === 'glass' || kind === 'facet' || kind === 'slope';
      const lit = kind === 'columbia' ? '#b08a5c' : cool && r() < 0.5 ? COOL_B : r() < 0.3 ? WARM_A : WARM_B;
      if (q < dense) p.set(i, y, q < dense * 0.45 ? lit : mixHex(lit, '#1c2a3b', 0.45));
      else if (q < dense + 0.1) wins.push({ id: wins.length, x: i, y, n: 2 + Math.floor(r() * 6), p: 0.5, off: r(), col: lit });
    }
  }
  // Columbia Center's twin masts, the lightning rods and crowns of the others
  for (const mx of [196, 201]) { p.set(mx, 62, '#2a3344'); p.set(mx, 63, '#2a3344'); }
  p.set(233, 71, '#c8b088'); p.set(233, 72, '#c8b088'); p.set(233, 73, '#e8d0a0');
  for (let i = 136; i < 148; i++) if (p.has(i, towerTop('slope', 135, 149, 70, i))) p.set(i, towerTop('slope', 135, 149, 70, i), '#8ab4d8');
  BEACONS.push([196, 61], [201, 61], [94, 71], [147, 68], [233, 70], [184, 79], [213, 87], [260, 95], [118, 95], [39, 97]);
  return { c: p.done(), wins };
}
const STEAMS = [[246, 103], [65, 91], [274, 115], [222, 109], [160, 121]];   // rooftop steam, downtown

/* ================= Elliott Bay ================= */
const BAY_X = 284;
const shoreY = x => Math.round(ridge([[BAY_X, 171], [300, 173], [322, 179], [352, 193], [404, 206], [480, 214]], x));   // the near shore
function renderBay() {
  const w = W - BAY_X, h = WALL + 4 - SHORE, p = painter(w, h);
  const P = ['#1c3640', '#172d37', '#13252f', '#101f28', '#0d1921', '#0a141b'];
  for (let y = 0; y < h; y++) for (let i = 0; i < w; i++) {
    let v = Math.pow(y / h, 0.7);
    v -= 0.3 * Math.max(0, 1 - Math.hypot((i - w) / 150, y / 30));    // the afterglow lies on the water in the west
    p.set(i, y, dpick(P, v, i, y));
  }
  return p.done();
}

/* ================= Belltown's flats, Uptown's roofs, Seattle Center and the waterfront (between downtown and the hill) ================= */
const BELL = [[196, 206, 130], [207, 219, 120], [221, 231, 136], [233, 246, 124], [248, 258, 140], [260, 272, 130], [274, 284, 144], [286, 296, 138]];
const ARCHES = [199, 205, 211];                      // the Science Center's white arches
const ARENA = { x0: 224, x1: 266, apex: 172, eave: 181 };
const PIER_LAMPS = [300, 308, 316, 328, 338, 348, 360, 374, 390];
function renderMid() {
  const p = painter(), r = rng(88), wins = [];
  const win = (x, y, col, pr = 0.3) => { const q = r(); if (q < pr) p.set(x, y, q < pr * 0.5 ? col : mixHex(col, '#141a26', 0.4)); else if (q < pr + 0.1) wins.push({ id: wins.length, x, y, n: 2 + Math.floor(r() * 6), p: 0.5, off: r(), col }); };
  // low blocks at the foot of downtown, from South Lake Union to the waterfront
  for (let x = 0; x < 300;) {
    const w = 5 + Math.floor(r() * 10), top = 141 + Math.floor(r() * 9);
    for (let y = top; y < 160; y++) for (let i = x; i < Math.min(300, x + w); i++) p.set(i, y, y === top ? '#2c3444' : i === x ? '#1c2230' : '#151a25');
    for (let y = top + 2; y < 158; y += 2) for (let i = x + 1; i < Math.min(299, x + w - 1); i += 2) win(i, y, r() < 0.7 ? WARM_B : COOL_B, 0.26);
    x += w;
  }
  // Belltown's towers of flats: nearer and warmer than the offices behind them
  for (const [x0, x1, top] of BELL) {
    for (let y = top; y < 164; y++) for (let i = x0; i <= x1; i++) {
      let col = dpick(['#141925', '#1a2030', '#222a3b', '#2c3649', '#384458'], 0.8 - 0.62 * (i - x0) / (x1 - x0) + 0.2 * Math.max(0, 1 - (y - top) / 14), i, y);
      if (y === top) col = '#55627a'; else if ((y - top) % 3 === 0) col = i === x0 ? '#2c3649' : '#10141d';   // balcony slabs
      p.set(i, y, col);
    }
    p.rect(x0 + 2, top - 2, 3, 2, '#1b2130');
    for (let y = top + 2; y < 162; y += 3) for (let i = x0 + 1; i < x1; i += 2) win(i, y, r() < 0.75 ? (r() < 0.5 ? WARM_A : WARM_B) : COOL_A, 0.4);
  }
  // Uptown: roofs stepping toward us, a few lit windows, the orange lamps of its streets
  for (let k = 0; k < 7; k++) {
    const y0 = 158 + k * 7, bh = 5 + k;
    for (let x = -Math.floor(r() * 12); x < 300;) {
      const w = 9 + k * 2 + Math.floor(r() * 12), top = y0 - Math.floor(r() * 4);
      for (let y = top; y < y0 + bh; y++) for (let i = Math.max(0, x); i < Math.min(300, x + w - 1); i++)
        p.set(i, y, y === top ? '#2a3140' : y === top + 1 ? '#1e2430' : i === x ? '#1a202c' : '#11161f');
      for (let y = top + 3; y < y0 + bh - 1; y += 3) for (let i = x + 2; i < Math.min(299, x + w - 2); i += 3) if (i > 0) win(i, y, r() < 0.8 ? WARM_B : COOL_B, 0.22);
      if (r() < 0.5 && x + w < 300 && x + w > 0) p.set(x + w - 1, y0 + bh - 2, '#f2a050');     // a street lamp in the gap
      x += w;
    }
  }
  // the bare trees of Seattle Center round the Needle's foot
  for (let n = 0; n < 16; n++) {
    const tx = 136 + Math.floor(r() * 66), ty = 184 + Math.floor(r() * 10), rr = 3 + r() * 3;
    for (let y = Math.floor(ty - rr); y <= ty + rr; y++) for (let i = Math.floor(tx - rr * 1.5); i <= tx + rr * 1.5; i++) {
      const v = 1 - ((i - tx) / (rr * 1.5)) ** 2 - ((y - ty) / rr) ** 2 + (hash(i, y, 21) - 0.5) * 0.6;
      if (v > 0) p.set(i, y, v > 0.5 ? '#0d1218' : hash(i, y, 22) < 0.25 ? '#3a3220' : '#141a20');
    }
  }
  // MoPOP's sheet-metal swoops, just left of the Needle's foot
  for (let i = 152; i < 165; i++) for (let y = 179 + Math.round(2 * Math.sin(i * 0.9)); y < 190; y++)
    p.set(i, y, dpick(i < 157 ? ['#4a1e3a', '#7a2e5a', '#a84a7c'] : i < 161 ? ['#6a5220', '#a88432', '#d8b458'] : ['#3a4658', '#6a7a92', '#9aaac0'], 0.8 - (y - 179) / 12, i, y));
  // the Science Center's five slender arches (three clear of the trees), floodlit white
  for (const ax of ARCHES) for (let k = 0; k <= 15; k++) {
    const y = 170 + k, hw = k < 4 ? k * 0.5 : 2;
    p.set(Math.round(ax - hw), y, '#e6ecf6'); p.set(Math.round(ax + hw), y, '#aab4c8');
    if (k === 0) p.set(ax, y - 1, '#f6f8fc');
  }
  // the arena's pyramid roof on its four buttresses, a lit band of glass under the eaves
  for (let y = ARENA.apex; y <= ARENA.eave; y++) {
    const u = (y - ARENA.apex) / (ARENA.eave - ARENA.apex), hw = Math.round(3 + u * 18), mid = (ARENA.x0 + ARENA.x1) >> 1;
    for (let i = mid - hw; i <= mid + hw; i++) p.set(i, y, i === mid - hw || i === mid ? '#6a7488' : i < mid ? '#3e4758' : '#2c3444');
  }
  for (let i = ARENA.x0; i <= ARENA.x1; i++) { p.set(i, ARENA.eave + 1, '#8a94a8'); for (let y = ARENA.eave + 2; y < ARENA.eave + 6; y++) p.set(i, y, (i - ARENA.x0) % 7 === 0 ? '#3a4252' : y < ARENA.eave + 4 ? '#ffdfa6' : '#e8b06a'); }
  // the waterfront: land below the shore line, pier sheds, the lamps of the shore road
  for (let x = BAY_X + 6; x < W; x++) {
    const top = shoreY(x);
    for (let y = top; y < WALL + 2; y++) p.set(x, y, y === top ? '#28303c' : (x + y * 3) % 7 === 0 ? '#1a202b' : '#11161f');
  }
  for (const [px0, len] of [[304, 9], [330, 8], [344, 10], [366, 9]]) {          // piers reaching out into the bay
    const y0 = shoreY(px0);
    for (let k = 0; k < len; k++) { const x = px0 + k, y = y0 - 1 - Math.round(k * 0.45); p.set(x, y, '#3a4250'); p.set(x, y + 1, '#1c222c'); p.set(x, y + 2, '#151a22'); if (k % 3 === 1) p.set(x, y + 1, '#e8b870'); }
  }
  for (const lx of PIER_LAMPS) p.set(lx, shoreY(lx) - 1, '#ffd89a');
  // the Great Wheel's pier and A-frame (its rim and lights are drawn per frame)
  const { cx, cy, r: wr } = WHEEL;
  for (let k = 0; k <= wr + 2; k++) { p.set(cx - Math.round(k * 0.5), cy + k, '#8a94a8'); p.set(cx + Math.round(k * 0.5), cy + k, '#5a6478'); }
  p.rect(cx - 8, cy + wr + 2, 17, 2, '#2a3240');
  return { c: p.done(), wins };
}
/* the Great Wheel: its rim's pixels, in order round the circle */
const WHEEL_RIM = [];
for (let k = 0; k < 56; k++) {
  const a = k / 56 * TAU, x = Math.round(WHEEL.cx + Math.sin(a) * WHEEL.r), y = Math.round(WHEEL.cy - Math.cos(a) * WHEEL.r);
  if (!WHEEL_RIM.some(q => q.x === x && q.y === y)) WHEEL_RIM.push({ x, y, a });
}
const WHEEL_COLS = ['#f4f0ff', '#33ff77', '#46b4ff', '#ffc24a', '#ff6fa8', '#33ff77', '#f4f0ff', '#46b4ff'];   // its light show, a colour every 30 s

/* ================= The Space Needle =================
   184 m: three pairs of slender legs pinched at the waist and flaring again under the saucer, the hexagonal core with
   the lifts on its outside, the small disc of the 100-foot level, the saucer (halo, restaurant, open deck, roof) and
   the spire with its beacon. Floodlit white from the foot, the saucer lit warm from inside. */
const NWH = ['#5e6880', '#7a849c', '#98a2ba', '#b8c1d4', '#d8deea', '#f4f6fb'];
const legHw = y => y <= ND.waist ? 4.5 + 8.5 * Math.pow((ND.waist - y) / (ND.waist - 54), 1.7) : 4.5 + 12.5 * Math.pow((y - ND.waist) / (ND.base - ND.waist), 1.45);
const DISC = [[36, 1], [37, 2], [38, 2], [39, 4], [40, 5], [41, 5], [42, 8], [43, 10], [44, 13], [45, 16], [46, 18], [47, 19], [48, 19],
  [49, 20], [50, 21], [51, 20], [52, 18], [53, 15], [54, 12], [55, 10], [56, 8], [57, 6]];
const HALO_Y = 50, HALO_HW = 21, SKY_LEVEL = 166;
function renderNeedle() {
  const p = painter(), cx = ND.cx;
  // the two outer pairs of legs: steel bands of even weight (a little heavier towards the foot) whose edges follow the
  // curve, a pixel only part covered drawn dimmer so the outline's steps soften. Lit from the left: the left leg's
  // outer face and the right leg's inner face catch the floodlights, the other faces fall into shadow.
  const shade = v => NWH[clamp(Math.round(v * (NWH.length - 1)), 0, NWH.length - 1)];
  for (let y = 54; y <= ND.base; y++) {
    const hw = legHw(y + 0.5);
    const lit = 0.55 + 0.4 * smooth(120, ND.base, y) + 0.25 * smooth(84, 56, y);       // floodlights at the foot, the saucer's glow above
    for (let i = cx - 1; i <= cx + 1; i++) p.set(i, y, dpick(['#2c3440', '#3a4452', '#4c5868', '#647084'], lit * 0.7 - (i - cx) * 0.2, i, y));   // the core
    if (hw > 5.2) p.set(cx + 2, y, dpick(NWH, lit * 0.8, cx + 2, y));                  // the third pair of legs, end on
    const wt = y < ND.waist ? 2.1 + 0.4 * smooth(ND.waist, 56, y) : 2.1 + 0.9 * (y - ND.waist) / (ND.base - ND.waist);
    const inner = [];
    for (const s of [-1, 1]) {
      const out = cx + s * (hw + 0.5), a = Math.min(out, out - s * wt), b = Math.max(out, out - s * wt);
      inner.push(out - s * wt);
      for (let i = Math.round(a); i <= Math.round(b); i++) {
        const cov = Math.min(b, i + 0.5) - Math.max(a, i - 0.5);
        if (cov < 0.3) continue;
        const u = clamp((s < 0 ? i - a : b - i) / wt, 0, 1);                           // 0 on the outer face, 1 on the inner
        let v = s < 0 ? lit - 0.42 * u : lit - 0.46 + 0.3 * u;
        if (cov < 0.65) v -= 0.3;
        p.set(i, y, shade(v));
      }
    }
    if (y === 104 || y === 124 || y === 146)                                           // ring braces
      for (let i = Math.ceil(inner[0]); i <= Math.floor(inner[1]); i++) if (Math.abs(i - cx) > 2 || hw < 5.2) p.set(i, y, '#6a7488');
  }
  {                                                                                    // the 100-foot level: a small lit disc
    const hw = Math.round(legHw(SKY_LEVEL)) + 2;
    for (let i = cx - hw; i <= cx + hw; i++) {
      p.set(i, SKY_LEVEL - 1, '#d8deea'); p.set(i, SKY_LEVEL, (i - cx) % 3 ? '#ffdc9c' : '#b08850'); p.set(i, SKY_LEVEL + 1, '#f0c078'); p.set(i, SKY_LEVEL + 2, '#5e6880');
    }
  }
  for (const [y, hw] of DISC) for (let i = cx - hw; i <= cx + hw; i++) {
    const dx = i - cx, u = Math.abs(dx) / Math.max(1, hw), side = dx < 0 ? 0.1 : -0.12;
    let col;
    if (y <= 38) col = y === 36 ? '#e4e9f2' : dx === 0 ? '#ffe6b0' : '#aab4ca';
    else if (y <= 41) col = y === 40 ? ((dx & 1) ? '#ffe6b0' : '#8a94a8') : y === 39 ? '#d8deea' : '#98a2ba';
    else if (y <= 43) col = dpick(NWH, 0.62 + side - (dx % 3 === 0 ? 0.2 : 0), i, y);
    else if (y <= 46) col = dpick(NWH, 0.82 + side - u * 0.25 - ((dx + 30) % 4 === 0 ? 0.22 : 0), i, y);   // the roof and its ribs
    else if (y === 47) col = '#fff6dc';                                                 // the open deck's glass
    else if (y === 48) col = (dx + 40) % 3 === 0 ? '#c89a58' : '#ffe2a0';
    else if (y === 49) col = (dx + 40) % 4 === 0 ? '#a87a48' : '#ffd890';               // the restaurant's windows
    else if (y === HALO_Y) col = Math.abs(dx) > 19 ? '#8a94a8' : '#8a7448';             // the halo (its lights are drawn per frame)
    else if (y === 51) col = (dx + 40) % 4 === 2 ? '#a87a48' : '#f0c070';
    else col = dpick(['#6a6860', '#8e8878', '#b4aa90', '#d8cba8', '#f2e6c4'], 0.95 - (y - 52) / 9 - u * 0.25 + side - ((dx + 30) % 4 === 0 ? 0.25 : 0), i, y);   // underside, lit from below
    p.set(i, y, col);
  }
  for (let y = ND.tip + 1; y < 36; y++) p.set(cx, y, y < 28 ? '#98a2ba' : '#b8c1d4');   // the spire
  for (const y of [30, 34]) { p.set(cx - 1, y, '#7a849c'); p.set(cx + 1, y, '#7a849c'); }
  return p.done();
}

/* ================= Vessels ================= */
/* a state ferry: double-ended, white with a green stripe, two decks of lit windows, a pilothouse at each end */
const FERRY_W = 46, FERRY_H = 12, GREEN = '#1f7a4c';
function renderFerry() {
  const w = FERRY_W, p = painter(w, FERRY_H);
  for (let y = 8; y < 12; y++) for (let i = (y - 8) * 2; i < w - (y - 8) * 2; i++) p.set(i, y, y === 8 ? '#e4e8ee' : y === 9 ? GREEN : y === 10 ? '#c4cad4' : '#18202a');
  for (let i = 2; i < w - 2; i++) {
    p.set(i, 6, '#dfe4ea'); p.set(i, 7, i % 4 < 2 && i > 4 && i < w - 5 ? '#27303a' : '#c4cad4');     // the car deck's openings
    if (i > 3 && i < w - 4) { p.set(i, 3, '#eef1f5'); p.set(i, 4, i % 3 ? '#ffe4a4' : '#aab2be'); p.set(i, 5, i % 3 === 1 ? '#ffd080' : '#d4d9e0'); }
    if (i > 5 && i < w - 6 && (i & 1)) p.set(i, 2, '#aab2be');                                         // the sun deck's rail
  }
  for (const x0 of [5, w - 10]) { p.rect(x0, 0, 5, 2, '#dfe4ea'); for (let i = x0; i < x0 + 5; i++) p.set(i, 1, i === x0 + 2 ? '#dfe4ea' : '#bfe0ff'); }
  p.rect(21, 0, 4, 3, '#e4e8ee'); p.rect(21, 1, 4, 1, GREEN); p.rect(21, 0, 4, 1, '#27303a');         // the funnel's green band
  return p.done();
}
/* a bulk carrier waiting at anchor for the grain terminal (bow left): hatches, deck cranes, the bridge aft */
const SHIP_W = 58, SHIP_H = 15;
function renderShip() {
  const w = SHIP_W, h = SHIP_H, p = painter(w, h), r = rng(77);
  for (let y = 8; y < h; y++) {
    const x0 = Math.max(0, 4 - (y - 8)), x1 = w - 1 - (y > 12 ? 1 : 0);
    for (let i = x0; i <= x1; i++) p.set(i, y, y === 8 ? '#4a4048' : y >= 13 ? '#5a2422' : (i + y) % 9 === 0 ? '#241e26' : '#1c1820');
  }
  for (let i = 3; i < 42; i++) p.set(i, 7, i % 9 < 7 ? '#3a3a44' : '#262630');                          // hatch covers
  for (const cx of [10, 21, 32]) { for (let y = 2; y < 7; y++) p.set(cx, y, '#4a4a56'); p.line(cx, 2, cx + 6, 5, '#3c3c48'); }
  for (let y = 1; y < 8; y++) for (let i = 44; i <= 54; i++) p.set(i, y, i === 44 ? '#9aa0aa' : i >= 53 ? '#565a66' : y === 1 ? '#aab0ba' : '#7a7e8a');
  for (const wy of [3, 5]) for (let i = 45; i < 53; i++) p.set(i, wy, (i & 1) ? (r() < 0.65 ? '#ffd48a' : '#2a2e36') : '#62666f');
  p.rect(48, 0, 3, 1, '#565a66'); p.rect(49, -1 + 1, 1, 1, '#c8642a');
  return p.done();
}

/* ================= The hill below the park: Queen Anne's wooden houses, a brick apartment block, firs, a steep street ================= */
const FIR = ['#05090a', '#080e0d', '#0c1512', '#111d17', '#18281f', '#213528'];
const ROOF = ['#15171d', '#1c1f26', '#252830', '#30343e', '#3e4350'];
const CHIMNEYS = [[52, 155], [301, 175], [373, 177]];             // [x, top] of the smoking chimney pots
const PORCH = [[58, 185], [331, 197], [419, 199], [246, 199]];    // porch lights
const NEAR_WINS = [];                                // windows that switch during the loop: { x, y, w, h, n, p, off, tv }
const streetAt = y => { const u = clamp((y - STREET.y0) / (STREET.y1 - STREET.y0), 0, 1); return { cx: lerp(STREET.x0, STREET.x1, u), hw: 1.5 + 12 * Math.pow(u, 1.35) }; };
const STREET_LAMPS = [0.12, 0.3, 0.5, 0.72].map((u, k) => { const y = Math.round(lerp(STREET.y0, STREET.y1, u)), s = streetAt(y), side = k & 1 ? 1 : -1; return { x: Math.round(s.cx + side * (s.hw + 1)), y: y - Math.round(2 + u * 6), u }; });
/** a Douglas fir: drooping tiers, a ragged edge, the sky's glow on its right, the lamp's on whatever faces it */
function fir(p, cx, top, base, hw, seed) {
  const tier = 7 + (seed % 3);
  for (let y = top; y < base; y++) {
    const u = (y - top) / (base - top), saw = ((y - top) % tier) / tier;
    const half = Math.max(0.6, hw * Math.pow(u, 0.8) * (0.62 + 0.38 * saw)) + (y - top < 4 ? 0 : hash(y, seed, 31) * 1.5);
    for (let i = Math.floor(cx - half); i <= Math.ceil(cx + half); i++) {
      const e = (i - cx) / (half + 0.01);
      if (Math.abs(e) > 1 || (Math.abs(e) > 0.75 && hash(i, y, seed) < 0.4)) continue;
      let v = 0.22 + 0.3 * Math.max(0, e) * (1 - u * 0.5) + (saw < 0.2 ? -0.15 : saw > 0.8 ? 0.16 : 0) + (hash(i >> 1, y >> 1, seed + 1) - 0.5) * 0.3;
      const warm = Math.max(0, 1 - Math.hypot((i - LAMP.x) / 46, (y - LAMP.y) / 30));
      p.set(i, y, warm > 0.25 && bayer(i, y) < warm ? (v > 0.3 ? '#4a4026' : '#2c2a1a') : dpick(FIR, v, i, y));
    }
  }
}
/* the two firs that stand clear of the roofs stir in the breeze: each is its own sprite, sheared row by row */
const FIR_DEFS = [{ cx: 12, top: 96, hw: 17, seed: 5, k: 9, ph: 0.1 }, { cx: 345, top: 160, hw: 8, seed: 9, k: 11, ph: 0.6 }];
function renderFir(d) { const p = painter(); fir(p, d.cx, d.top, NEAR_BOT, d.hw, d.seed); return { ...d, L: trim(p.done()) }; }
function renderNear(bare) {                           // bare: without the sign's glow on the roof
  const p = painter(), r = rng(123);
  NEAR_WINS.length = 0;                               // (both versions list the same windows)
  const glass = (x, y, w, h, kind) => {              // kind: 1 lit, 0 dark, 2 switches, 3 a television
    p.rect(x - 1, y - 1, w + 2, h + 2, '#0c0e13');
    for (let j = 0; j < h; j++) for (let i = 0; i < w; i++) p.set(x + i, y + j, kind === 1 ? (j < h / 2 ? '#ffe0a0' : '#f0b060') : (i + j) % 5 === 0 ? '#1c222e' : '#11151d');
    if (kind === 1 && w > 2) for (let j = 0; j < h; j++) p.set(x + (w >> 1), y + j, '#a8763e');
    if (kind >= 2) NEAR_WINS.push({ id: NEAR_WINS.length, x, y, w, h, n: 2 + Math.floor(r() * 4), p: 0.55, off: r(), tv: kind === 3 });
  };
  const roofPx = (i, y, top, wet) => dpick(ROOF, 0.3 + wet * Math.max(0, 1 - (y - top) / 5) + ((y - top) % 3 === 0 ? -0.12 : 0) + (hash(i >> 1, y, 41) - 0.5) * 0.25, i, y);
  /** a side-gabled house seen from uphill: its long roof toward us, a strip of wall under the eave */
  const sideHouse = (x0, x1, ridge, eave, wall, trim, wins) => {
    for (let y = ridge; y < NEAR_BOT; y++) for (let i = x0; i <= x1; i++) {
      const inset = y < eave ? Math.round((eave - y) * 0.25) : 1;
      if (i < x0 + inset || i > x1 - inset) continue;
      p.set(i, y, y === ridge ? '#565e70' : y < eave ? roofPx(i, y, ridge, 0.5) : y === eave ? trim : (i - x0) % 2 === 0 ? wall : mixHex(wall, '#000000', 0.18));
    }
    for (const [wx, wy, ww, wh, k] of wins) glass(wx, wy, ww, wh, k);
  };
  /** a front-gabled house: its gable end toward us, bargeboards, an attic window */
  const gableHouse = (x0, x1, apex, eave, wall, trim, wins) => {
    const mid = (x0 + x1) >> 1;
    for (let y = apex; y < NEAR_BOT; y++) {
      const hw = y < eave ? Math.round((y - apex) / (eave - apex) * (mid - x0)) : mid - x0 - 1;
      for (let i = mid - hw; i <= mid + hw; i++) {
        const edge = y < eave && (i === mid - hw || i === mid + hw || i === mid - hw + 1 || i === mid + hw - 1);
        p.set(i, y, edge ? (i < mid ? trim : mixHex(trim, '#000000', 0.35)) : (y - apex) % 2 === 0 ? wall : mixHex(wall, '#000000', 0.16));
      }
    }
    p.set(mid, apex - 1, trim);
    for (const [wx, wy, ww, wh, k] of wins) glass(wx, wy, ww, wh, k);
  };
  const chimney = (x, top, h) => { for (let y = top; y < top + h; y++) for (let i = x - 1; i <= x + 1; i++) p.set(i, y, y === top ? '#6a5a54' : i === x + 1 ? '#2a2020' : (y + i) % 3 === 0 ? '#3a2826' : '#4a3430'); p.set(x, top - 1, '#3a3034'); };

  // the steep street dropping toward the bay: wet asphalt, kerbs, a broken centre line
  for (let y = STREET.y0; y <= STREET.y1 + 1; y++) {
    const s = streetAt(y), xa = Math.round(s.cx - s.hw), xb = Math.round(s.cx + s.hw);
    for (let i = xa; i <= xb; i++) {
      let col = dpick(['#10141c', '#161b25', '#1d2330', '#27303e'], 0.3 + 0.5 * Math.max(0, 1 - Math.abs(i - s.cx) / (s.hw + 1)) * ((y & 1) ? 1 : 0.6), i, y);
      if (i === xa || i === xb) col = '#39424f';
      if (Math.round(s.cx) === i && s.hw > 3 && (y % 4 < 2)) col = '#8a7a4a';
      p.set(i, y, col);
    }
  }
  for (const l of STREET_LAMPS) { for (let y = l.y; y < l.y + 3 + Math.round(l.u * 5); y++) p.set(l.x, y, '#1c222c'); p.set(l.x, l.y, '#ffc070'); }
  // houses left of the apartment block
  sideHouse(22, 60, 163, 175, '#26302a', '#7c8272', [[28, 180, 5, 6, 1], [38, 180, 5, 6, 2], [49, 181, 4, 6, 0], [34, 166, 6, 5, 2]]);
  for (let i = 32; i <= 41; i++) { p.set(i, 164, '#565e70'); p.set(i, 165, '#0c0e13'); }                // the dormer's little roof
  chimney(52, 155, 10);
  fir(p, 80, 138, NEAR_BOT, 9, 7);
  gableHouse(60, 92, 168, 183, '#3a2222', '#8a7c6c', [[73, 174, 5, 5, 1], [65, 188, 5, 7, 2], [80, 188, 5, 7, 0]]);
  // the brick apartment block (1920s): a stone-capped parapet, sash windows, a fire escape, a stair bulkhead
  for (let y = APT.roof; y < NEAR_BOT; y++) for (let i = APT.x0; i <= APT.x1; i++) {
    const row = Math.floor(y / 2), mortar = (i + (row & 1) * 2) % 4 === 0;
    let col = mortar ? '#2a1a1c' : ['#3c2426', '#462a2a', '#50302c'][Math.floor(hash(i >> 2, row, 51) * 3)];
    if (y === APT.roof) col = '#8a8690'; else if (y === APT.roof + 1) col = '#565460'; else if (y === APT.roof + 2) col = '#241618';
    if (i === APT.x0) col = '#5a3a34'; else if (i === APT.x1) col = '#1c1214';
    const g = bare ? 0 : Math.max(0, 1 - Math.hypot((i - (BILL.x + 23)) / 30, (y - APT.roof) / 9));       // the sign's green on the bricks
    if (g > 0.15 && bayer(i, y) < g * 0.9 && y > APT.roof + 2) col = mixHex(col, '#2cc868', 0.3);
    p.set(i, y, col);
  }
  for (let j = 0; j < 5; j++) for (let k = 0; k < 7; k++) {
    const wx = APT.x0 + 5 + k * 8, wy = APT.roof + 7 + j * 11, q = r();
    if (k === 3) continue;                                                                                // the stair's column
    p.rect(wx - 1, wy - 2, 6, 1, '#7a7480'); p.rect(wx - 1, wy + 6, 6, 1, '#5a5460');
    glass(wx, wy, 4, 6, q < 0.3 ? 1 : q < 0.55 ? 2 : q < 0.62 ? 3 : 0);
  }
  for (let y = APT.roof + 5; y < NEAR_BOT; y++) {                                                             // the fire escape
    const k = (y - APT.roof - 5) % 11, fx = APT.x0 + 27;
    p.set(fx, y, '#0c0d12'); p.set(fx + 7, y, '#0c0d12');
    if (k === 8) for (let i = fx; i <= fx + 7; i++) p.set(i, y, '#1a1c24'); else if (k < 8) p.set(fx + 7 - k, y, '#14161c');
  }
  p.rect(APT.x1 - 7, APT.roof - 6, 7, 6, '#3a2628'); p.rect(APT.x1 - 7, APT.roof - 6, 7, 1, '#6a6470'); p.set(APT.x1 - 4, APT.roof - 3, '#ffd890');   // bulkhead, its lamp
  p.rect(APT.x0 + 2, APT.roof - 3, 2, 3, '#3a3a44');                                                     // a vent
  // the middle: shrubs and bare crowns along the foot of the frame, low enough to leave the Needle's legs clear
  for (let x = 152; x < 240; x++) {
    const top = 199 + Math.round(3 * Math.sin(x * 0.31) + 2 * Math.sin(x * 0.13 + 1) + hash(x, 0, 61) * 2);
    for (let y = top; y < NEAR_BOT; y++) p.set(x, y, hash(x, y, 62) < 0.12 ? '#4a3a1c' : dpick(FIR, 0.2 + (hash(x >> 1, y >> 1, 63) - 0.5) * 0.4 + (y === top ? 0.3 : 0), x, y));
  }
  fir(p, 197, 186, NEAR_BOT, 5, 11);
  // right of the street
  sideHouse(236, 262, 187, 195, '#3c3a30', '#8a8672', [[241, 198, 5, 6, 2], [252, 198, 5, 6, 1]]);
  gableHouse(292, 336, 181, 193, '#222c3a', '#7e8694', [[311, 185, 6, 5, 2], [298, 197, 5, 7, 1], [321, 197, 5, 7, 0]]);
  chimney(301, 175, 9);
  sideHouse(358, 420, 185, 196, '#4a3c20', '#8c8468', [[366, 199, 5, 6, 1], [380, 199, 5, 6, 2], [398, 199, 5, 6, 0], [366, 188, 6, 5, 2], [396, 188, 6, 5, 1]]);
  for (const dx of [364, 394]) for (let i = dx; i <= dx + 9; i++) { p.set(i, 186, '#565e70'); p.set(i, 187, '#0c0e13'); }
  chimney(373, 177, 9);
  gableHouse(432, 482, 190, 201, '#1e2c26', '#7a8478', [[454, 194, 6, 5, 2]]);
  // a bigleaf maple, nearly bare: the last yellow leaves hang on in November
  const limb = (x0, y0, ang, len, wd, depth) => {
    let xx = x0, yy = y0, a = ang;
    for (let s = 0; s < len; s++) {
      a += (r() - 0.5) * 0.3; xx += Math.cos(a); yy += Math.sin(a);
      p.set(Math.round(xx), Math.round(yy), '#0d0f12'); if (wd > 1) p.set(Math.round(xx) + 1, Math.round(yy), '#1a1c20');
      if (depth < 3 && s > 3 && r() < 0.16) limb(xx, yy, a + (r() < 0.5 ? 0.7 : -0.7), len * 0.55, 1, depth + 1);
      if (depth > 0 && r() < 0.2) { const lc = ['#c89a2a', '#e0b83a', '#b8742a'][Math.floor(r() * 3)]; p.set(Math.round(xx), Math.round(yy) + 1, lc); if (r() < 0.5) p.set(Math.round(xx) + 1, Math.round(yy) + 1, lc); }
    }
  };
  limb(474, NEAR_BOT, -1.6, 30, 2, 0); limb(474, 194, -2.3, 24, 1, 1); limb(475, 190, -0.9, 14, 1, 1); limb(474, 184, -1.6, 24, 1, 1); limb(473, 188, -2.7, 18, 1, 1);
  for (const [lx, ly] of PORCH) { p.set(lx, ly, '#fff0c8'); p.set(lx, ly + 1, '#ffb860'); }
  return p.done();
}

/* ================= The rooftop sign (the city's one billboard): a lit box on a steel frame on the apartment's roof ================= */
const STEEL = { hi: '#6a7284', mid: '#3e4554', dk: '#252a35', vdk: '#14171e' };
function renderBillFrame() {
  const p = painter();
  const L = BILL.x - 1, Rr = BILL.x + BILL.w, T = BILL.y - 1, B = BILL.y + BILL.h;
  for (let i = L; i <= Rr; i++) { p.set(i, T, STEEL.hi); p.set(i, B, STEEL.dk); }
  for (let y = T + 1; y <= B; y++) { p.set(L, y, STEEL.mid); p.set(Rr, y, STEEL.vdk); }
  p.rect(BILL.x, BILL.y, BILL.w, BILL.h, '#07120e');
  for (let i = L - 1; i <= Rr + 1; i++) p.set(i, B + 1, (i & 1) ? STEEL.mid : STEEL.hi);               // the catwalk
  for (const lx of [L + 4, L + 23, Rr - 4]) for (let y = B + 2; y < APT.roof; y++) { p.set(lx, y, STEEL.hi); p.set(lx + 1, y, STEEL.mid); }
  for (let k = 0; k < 3; k++) { p.set(L + 6 + k * 5, B + 2 + k, STEEL.mid); p.set(L + 21 - k * 5, B + 2 + k, STEEL.mid); p.set(L + 25 + k * 5, B + 2 + k, STEEL.mid); p.set(Rr - 6 - k * 5, B + 2 + k, STEEL.mid); }
  return p.done();
}
/* the city's own ad: MOSS RECORDS, a neighbourhood record shop: a turning record, the name in emerald tubes */
const DISC_C = { x: 8, y: 8, r: 6.5 };
function renderPoster() {
  const { w, h } = BILL, p = painter(w, h);
  for (let j = 0; j < h; j++) for (let i = 0; i < w; i++) p.set(i, j, dpick(['#07120e', '#0b1a14', '#10241b'], 0.2 + 0.5 * Math.max(0, 1 - Math.hypot((i - 30) / 22, (j - 7) / 9)), i, j));
  for (let j = 0; j < h; j++) for (let i = 0; i < 16; i++) {
    const d = Math.hypot(i - DISC_C.x, j - DISC_C.y);
    if (d > DISC_C.r) continue;
    p.set(i, j, d < 1 ? '#0a0a0e' : d < 2.7 ? (j < DISC_C.y ? '#ffa24a' : '#e8782c') : d > 5.5 ? '#444a5c' : d > 3.6 && d < 4.6 ? '#30333f' : '#1e2029');
  }
  const c = p.done(), x = c.getContext('2d');
  text(x, 'RECORDS', 18, 12, '#f3ead6');
  return c;
}
function bigText(s, col, hi, glow) {                  // FONT3 at 2x with a lit top and a soft glow under it
  const w = s.length * 4, [c, x] = makeCanvas(w * 2 + 1, 11);
  const mk = colr => { const [t, tx] = makeCanvas(w, 5); text(tx, s, 0, 0, colr); return t; };
  x.drawImage(mk(glow), 0, 0, w, 5, 1, 1, w * 2, 10);
  x.drawImage(mk(col), 0, 0, w, 5, 0, 0, w * 2, 10);
  x.drawImage(mk(hi), 0, 0, w, 1, 0, 0, w * 2, 2);
  return c;
}

/* ================= Kerry Park's terrace: the railing on its low wall, wet paving, puddles, fallen maple leaves ================= */
const PUDDLES = [[150, 233, 22, 3], [262, 227, 16, 2], [208, 251, 30, 4], [96, 262, 26, 4], [372, 257, 28, 4], [448, 235, 18, 3],
  [320, 264, 22, 3], [22, 241, 14, 3], [434, 263, 20, 3], [40, 263, 16, 3], [150, 261, 18, 3], [262, 262, 20, 3], [470, 252, 12, 3], [384, 229, 14, 2]];
const inPuddle = (i, y) => PUDDLES.some(([cx, cy, rx, ry]) => ((i - cx) / rx) ** 2 + ((y - cy) / ry) ** 2 < 1);
const MAPLE = ['#c89a2a', '#d86a28', '#a83a26', '#e0b83a'];
function renderGround() {
  const p = painter(), r = rng(31);
  for (let i = 0; i < W; i++) { p.set(i, RAIL, '#8590a4'); p.set(i, RAIL + 1, '#3a4250'); p.set(i, RAIL + 3, '#2c3340'); }   // top rail, mid rail
  for (let x = 8; x < W; x += 32) for (let y = RAIL; y < WALL; y++) { p.set(x, y, '#49515f'); p.set(x + 1, y, '#1e232c'); }
  for (let y = WALL; y < FOOT; y++) for (let i = 0; i < W; i++) {                                                             // the concrete wall
    const warm = Math.max(0, 1 - Math.abs(i - LAMP.x) / 90);
    let col = dpick(['#262b36', '#2e3440', '#373e4c', '#424a5a'], 0.45 + 0.25 * Math.sin(i * 0.05) * Math.sin(i * 0.017 + 2) - (y - WALL) / 30 + (hash(i >> 2, y >> 1, 71) - 0.5) * 0.3, i, y);
    if (y === WALL) col = '#7d8698'; else if (y === WALL + 1) col = '#59616f';
    else if (i % 48 === 20) col = '#1a1e27';
    else if (hash(i, 0, 72) < 0.1 && y < WALL + 3 + hash(i, 1, 72) * 9) col = '#20242d';                                      // damp streaks under the cap
    if (y === FOOT - 1) col = '#12151c';
    if (warm > 0 && y > WALL + 1 && bayer(i, y) < warm * 0.8) col = mixHex(col, '#a07a4c', 0.14 + 0.16 * warm);
    if (y === WALL && warm > 0.3) col = mixHex(col, '#ffd9a0', 0.3 * warm);
    p.set(i, y, col);
  }
  const rows = [[FOOT, 3], [221, 4], [225, 5], [230, 6], [236, 7], [243, 8], [251, 9], [260, 10]];
  rows.forEach(([y0, rh], ri) => {
    const sw = 16 + ri * 4;
    let x = -Math.floor(r() * sw);
    while (x < W) {
      const w = sw + Math.floor(r() * 5), tone = Math.floor(r() * 3);
      for (let y = y0; y < Math.min(H, y0 + rh); y++) for (let i = Math.max(0, x); i < Math.min(W, x + w); i++) {
        let col = ['#161a23', '#1a1f29', '#1e232e'][tone];
        const joint = i === x || y === y0 + rh - 1;
        if (joint) col = '#0d1017'; else if (y === y0) col = '#2a313e';                                                      // the wet top edge catches the sky
        const pool = Math.max(0, 1 - Math.hypot((i - LAMP.x) / 84, (y - 244) / 24));
        if (pool > 0 && !joint && bayer(i, y) < pool * 0.95) col = y === y0 ? (pool > 0.3 ? '#8a6c48' : '#4a3c2e') : pool > 0.5 ? '#54422f' : '#352c26';
        if (inPuddle(i, y)) col = !inPuddle(i, y - 1) ? '#465066' : !inPuddle(i, y + 1) ? '#1c212b' : '#0a0d14';
        p.set(i, y, col);
      }
      x += w;
    }
  });
  for (let n = 0; n < 34; n++) {                                                                                             // leaves stuck to the wet stone
    const x = 6 + Math.floor(r() * 468), y = 223 + Math.floor(r() * 44), col = MAPLE[Math.floor(r() * MAPLE.length)];
    p.set(x, y, col); p.set(x - 1, y, col); p.set(x + 1, y, col); p.set(x, y - 1, col);
    if (r() < 0.5) { p.set(x - 1, y - 1, col); p.set(x + 1, y - 1, col); }
    p.set(x, y + 1, mixHex(col, '#000000', 0.55));
  }
  return p.done();
}
/* the park lamp and "Changing Form" (Doris Chase, 1971): two hollow steel blocks pierced by great round openings */
function renderProps() {
  const p = painter(), X = LAMP.x, I = '#10141a', IH = '#343b48', IW = '#7a6040';
  for (let y = LAMP.y + 7; y <= LAMP.base; y++) { p.set(X, y, I); p.set(X - 1, y, y < LAMP.y + 30 ? IW : IH); }
  p.rect(X - 3, LAMP.base - 3, 6, 4, I); p.rect(X - 3, LAMP.base - 3, 6, 1, IH); p.rect(X - 2, LAMP.base - 6, 4, 3, I);
  // its lantern: a dark cap and finial, a tapering glass body lit from inside, a collar
  p.rect(X - 1, LAMP.y + 5, 3, 3, I); p.rect(X - 2, LAMP.y + 4, 5, 1, IH);
  p.set(X, LAMP.y - 9, IH); p.rect(X - 1, LAMP.y - 8, 3, 1, I); p.rect(X - 2, LAMP.y - 7, 5, 1, I); p.rect(X - 4, LAMP.y - 6, 9, 1, I); p.set(X - 4, LAMP.y - 6, IH);
  for (let y = LAMP.y - 5; y <= LAMP.y + 3; y++) {
    const k = y - (LAMP.y - 5), hw = k < 3 ? 3 : k < 6 ? 2 : 1;
    for (let i = X - hw; i <= X + hw; i++) p.set(i, y, Math.abs(i - X) === hw ? (k < 3 ? '#e8b060' : '#c88a40') : k < 2 ? '#fffaf0' : k < 5 ? '#ffeec4' : '#ffd088');
    p.set(X - hw - 1, y, k % 3 === 2 ? IH : I); p.set(X + hw + 1, y, I);
  }
  // the sculpture
  const fx = FORM.x, ST = ['#0e0b0d', '#181214', '#241a1b', '#33241f', '#4a3428'];
  const blocks = [{ x0: 3, x1: 33, y0: 26, y1: 49, hx: 18, hy: 38, hr: 8.6 }, { x0: 7, x1: 30, y0: 2, y1: 25, hx: 19, hy: 14, hr: 7.6 }];
  for (const b of blocks) for (let y = b.y0; y <= b.y1; y++) for (let i = b.x0; i <= b.x1; i++) {
    const cxn = Math.min(i - b.x0, b.x1 - i), cyn = Math.min(y - b.y0, b.y1 - y);
    if (cxn + cyn < 2) continue;                                                       // rounded corners
    const d = Math.hypot(i - b.hx, y - b.hy), d2 = Math.hypot(i - b.hx - 2.2, y - b.hy + 1.2);
    if (d < b.hr && d2 < b.hr) continue;                                               // the opening: the city shows through
    let v = 0.3 + 0.25 * (1 - (i - b.x0) / (b.x1 - b.x0)) + (hash(i, y >> 2, 81) - 0.5) * 0.25;
    if (d < b.hr) v = 0.85 - (y - b.hy + b.hr) / (b.hr * 3);                           // the inside of the hollow, lit by the city beyond
    else if (y === b.y0 || cxn + cyn === 2) v = 0.9;                                   // wet top edges
    else if (d < b.hr + 1.2) v -= 0.25;
    p.set(fx + i, FORM.top + y, dpick(ST, v, i, y));
  }
  for (let y = 0; y < 24; y++) { p.set(fx + 4 + (y > 20 ? 1 : 0), FORM.top + 2 + y, ST[1]); p.set(fx + 5, FORM.top + 2 + y, ST[2]); p.set(fx + 6, FORM.top + 2 + y, ST[3]); }   // the upper block's turned side
  p.rect(fx + 11, FORM.top + 50, 14, 4, ST[1]); p.rect(fx + 11, FORM.top + 50, 14, 1, ST[3]);
  return p.done();
}
/* a park bench seen from behind: three slats, iron ends */
function renderBench() {
  const w = BENCH.x1 - BENCH.x0 + 1, p = painter(w, 18);
  for (const y of [4, 7]) for (let i = 1; i < w - 1; i++) { p.set(i, y, y === 4 ? '#7a644a' : '#5a4634'); p.set(i, y + 1, '#33281f'); }
  for (let i = 0; i < w; i++) { p.set(i, 10, '#4a3a2c'); p.set(i, 11, '#1c1612'); }
  for (const ex of [0, w - 2]) { for (let y = 3; y < 17; y++) { p.set(ex, y, '#141820'); p.set(ex + 1, y, '#2c333f'); } p.rect(ex - 1 < 0 ? 0 : ex - 1, 16, 3, 1, '#141820'); }
  for (let y = 4; y < 10; y++) p.set(w >> 1, y, '#141820');
  return p.done();
}
function renderVignette() {
  const [c, x] = makeCanvas(W, H); const img = x.createImageData(W, H), d = img.data;
  for (let y = 0; y < H; y++) for (let i = 0; i < W; i++) {
    const v = smooth(0.72, 1.45, Math.hypot((i - W / 2) / (W / 2), (y - H / 2) / (H / 2) * 0.9));
    const q = Math.floor(v * 4 + bayer(i, y)) / 4;
    if (q <= 0) continue;
    const o = (y * W + i) * 4; d[o] = 4; d[o + 1] = 8; d[o + 2] = 14; d[o + 3] = Math.round(q * 0.45 * 255);
  }
  x.putImageData(img, 0, 0);
  return c;
}

/* ================= Creatures ================= */
const CROW_KEY = { k: '#0a0c12', g: '#1e222c', b: '#2c303a' };
const CROW_SIT = [['..kk.', '.kkkb', 'kkkk.', 'gkk..', '.g...'], ['.kk..', 'bkkk.', '.kkkk', '..kkg', '...g.']];   // facing right, facing left
const CROW_FLY = [['k.....k', '.kk.kk.', '...k...'], ['.......', 'kkkkkkk', '...k...'], ['.......', '.kkkkk.', 'k..k..k']];
const GULL_KEY = { w: '#e6eaf2', g: '#8a96a8', k: '#2a2e38', y: '#e8b830', s: '#c4cad4' };
const GULL_ROWS = [['k.............k', '.g...........g.', '.gw.........wg.', '..gw.......wg..', '...ww.....ww...', '....wwwwwwwwwy.', '......ssss.....'],
  ['...............', 'kgg.........ggk', '..ggww...wwgg..', '....wwwwwwwwwy.', '......ssss.....'],
  ['....wwwwwwwwwy.', '...ww.ssss.ww..', '..gw.......wg..', '.gg.........gg.', 'k.............k']];
const DOG_KEY = { d: '#2a2420', b: '#5a4630', w: '#d8d0c0', r: '#c83a34' };
const DOG_ROWS = [['.......bb.', 'b.....bbbb', '.brbbbbbw.', '.bbbbbbb..', '.b.b..b.b.', '.b.b..b.b.'], ['.......bb.', 'b.....bbbb', '.brbbbbbw.', '.bbbbbbb..', '..bb..bb..', '.b..b.b..b']];
const BIKE_KEY = { k: '#5a6478', s: '#3a4252', r: '#b8402e', w: '#9aa4b8' };
const BIKE_ROWS = ['..............w.....', '............www.....', '.....sss.....r......', '......rrrrrrrr......', '......rr....r.r.....', '..kkkr.r...r..rkkk..',
  '.k...r..r..r..kr..k.', 'k...r.k.r.r..k.r...k', 'k..rrrrrrr...k..r..k', 'k.....k......k.....k', '.k...k........k...k.', '..kkk..........kkk..'];
const UMB_ROWS = ['.....bbbbb.....', '...bbbbbbbbb...', '.bbBbbbBbbbBbb.', 'bbBbbbbBbbbbBbb', 'B.............B'], UMB_KEY = { b: '#6aa4c8', B: '#3a6a8c' };

/* ================= People: a few, in hooded rain jackets (nobody local bothers with an umbrella) ================= */
const NIGHT = '#0b111c', WARMC = '#ffd9a0', COOLC = '#a6b8d8';
const SKINS = ['#f0c8a0', '#dca880', '#b88458', '#8a5a3c'];
/** a look: height in px, jacket and trousers; o: skin 0..3, hood (false: down), hair, hat (a beanie), pack, strip (a
    reflective band), dk (how far into the night), warm (lit by the lamp rather than the city) */
function look(h, top, bot, o = {}) {
  const dk = o.dk ?? 0.3, m = c => mixHex(c, NIGHT, dk);
  return { h, hh: h >= 22 ? 5 : 4, hw: h >= 19 ? 4 : 3, th: Math.round(h * 0.38), tw: h >= 24 ? 7 : h >= 19 ? 6 : h >= 15 ? 5 : 4,
    skin: m(SKINS[o.skin ?? 0]), top: m(top), topD: mixHex(top, NIGHT, dk + 0.28), topL: mixHex(top, o.warm ? WARMC : COOLC, 0.42),
    bot: m(bot), shoe: m('#1a1a22'), hood: o.hood !== false, hair: m(o.hair || '#2a1c16'), hat: o.hat ? m(o.hat) : null,
    pack: o.pack ? m(o.pack) : null, strip: o.strip || null };
}
let PH = { hy: 0, ty: 0, tx: 0, bw: 0 };              // the last person drawn: head top, shoulder row, torso's left column and width
/**
 * One person, feet on row fy, centred on column x.
 * o.view: 0 from behind (looking at the city), 1 facing us, 2 in profile (o.dir: 1 right, -1 left)
 * o.step: stride frame (profile), o.sit: seated (fy is the seat), o.lean: head px towards a neighbour
 * o.aL / o.aR: 0 hands in the pockets, 1 up, 2 bent up to the face (a cup, a phone, a camera), 3 out (pointing)
 */
function person(x, fy, P, o = {}) {
  x = Math.round(x); fy = Math.round(fy);
  const { hw, hh, tw, th } = P, view = o.view || 0, dir = o.dir || 1, side = view === 2;
  const legs = o.sit ? 0 : P.h - hh - th, ty = fy - legs - th + 1, hy = ty - hh, bw = side ? tw - 2 : tw, tx = x - (bw >> 1);
  if (legs) {
    const ly = fy - legs + 1;
    if (side) {
      if (o.step) {
        R(x - 2, ly, 4, 2, P.bot);
        R(dir > 0 ? x + 1 : x - 2, ly + 2, 2, legs - 2, P.bot); R(dir > 0 ? x + 1 : x - 3, fy, 3, 1, P.shoe);
        R(dir > 0 ? x - 2 : x + 1, ly + 2, 2, legs - 3, P.bot); R(dir > 0 ? x - 2 : x + 1, fy - 1, 2, 1, P.shoe);
      } else { R(x - 1, ly, 2, legs, P.bot); R(dir > 0 ? x - 1 : x - 2, fy, 3, 1, P.shoe); }
    } else {
      const lw = bw >= 7 ? 3 : 2;
      R(tx, ly, bw, 2, P.bot); R(tx, ly + 2, lw, legs - 2, P.bot); R(tx + bw - lw, ly + 2, lw, legs - 2, P.bot);
      R(tx, fy, lw, 1, P.shoe); R(tx + bw - lw, fy, lw, 1, P.shoe);
    }
  }
  R(tx, ty, bw, th + (legs ? 1 : 0), P.top);                                          // the jacket hangs over the hips
  if (!side) R(tx + bw - 1, ty + 1, 1, th - 1, P.topD);
  R(tx, ty, bw, 1, P.topL);                                                           // wet shoulders catch the light
  if (P.strip) R(tx, ty + 3, bw, 1, P.strip);
  if (P.pack) { if (view === 0) R(tx + 1, ty + 1, bw - 2, th - 2, P.pack); else if (side) R(dir > 0 ? tx - 1 : tx + bw - 1, ty + 1, 2, th - 2, P.pack); }
  if (side) {
    const st = o.aR || 0;
    if (st === 0) R(x + (o.step ? -dir : 0), ty + 1, 1, th - 1, P.topD);
    else if (st === 1) for (let k = 0; k < 4; k++) R(x + dir * (1 + k), ty + 1 - k, 1, 1, k < 3 ? P.topD : P.skin);
    else if (st === 2) { for (let k = 0; k < 3; k++) R(x + dir * (1 + k), ty + 2 - k, 1, 1, P.topD); R(x + dir * 3, ty - 1, 1, 1, P.skin); }
    else { R(dir > 0 ? x + 1 : x - 4, ty + 2, 4, 1, P.topD); R(x + dir * 5, ty + 2, 1, 1, P.skin); }
  } else {
    const arm = (ax, st, sg) => {
      if (!st) R(ax, ty + 1, 1, th - 1, P.topD);
      else if (st === 1) { R(ax, hy, 1, ty - hy + 2, P.topD); R(ax, hy - 2, 1, 2, P.skin); }
      else if (st === 2) { R(ax, hy + 2, 1, ty - hy, P.topD); R(ax, hy + 1, 1, 1, P.skin); }
      else { R(sg < 0 ? ax - 3 : ax, ty + 1, 4, 1, P.topD); R(sg < 0 ? ax - 4 : ax + 4, ty + 1, 1, 1, P.skin); }
    };
    arm(tx - 1, o.aL, -1); arm(tx + bw, o.aR, 1);
  }
  const hx = x - (hw >> 1) + (o.lean || 0);
  if (P.hood) {                                                                       // hood up: the jacket's colour, a face inside it
    R(hx, hy, hw, hh, P.top); R(hx, hy, hw, 1, P.topL);
    if (view === 0) R(hx, hy + hh - 1, hw, 1, P.topD);
    else if (view === 1) R(hx + 1, hy + 1, hw - 2, hh - 2, P.skin);
    else R(dir > 0 ? hx + hw - 2 : hx, hy + 1, 2, hh - 2, P.skin);
  } else {
    const cap = P.hat || P.hair;
    if (view === 0) { R(hx, hy, hw, hh, cap); R(hx + 1, hy + hh - 1, hw - 2, 1, P.skin); }
    else { R(hx, hy, hw, hh, P.skin); R(hx, hy, hw, P.hat ? 2 : 1, cap); if (side) R(dir > 0 ? hx : hx + hw - 1, hy, 1, hh - 1, cap); }
  }
  PH = { hy, ty, tx, bw };
  return hy;
}

/* ================= The loop's timeline (seconds) ================= */
const FERRY = [{ t0: 20, t1: 92, dir: 1 }, { t0: 150, t1: 222, dir: -1 }];   // out to the island, and the other boat coming in
const FERRY_PATH = { x0: 286, y0: 177, x1: 488, y1: 166 };                   // the dock behind Belltown's towers; off to the west
const HORNS = [21, 151];
const BELLS = [46, 133, 206];                        // the bell buoy, on a longer swell
const SEAPLANE = { t0: 58, t1: 86 };
const AIRLINER = { t0: 166, t1: 214 };
const CROWS_T = [[6, 30], [126, 150]];               // the evening flight to the roost
const GULLS_T = [{ a: 98, b: 108, dir: 1, y0: 78, dip: 14 }, { a: 196, b: 206, dir: -1, y0: 62, dip: 18 }];
const GAP = { a: 96, b: 152, f: 16 };                // the cloud opens on Mount Rainier
const TROLLEY = [{ t0: 116, t1: 130, up: false }, { t0: 224, t1: 237, up: true }];
const CAR_UP = { every: 40, from: 5, dur: 9 }, CAR_DOWN = { every: 60, from: 22, dur: 10 };
const SIGN_FLICK = [[64.2, 65.6], [187.4, 188.3]];
const LAMP_FLICK = [[142, 143.2]];
const DOGWALK = [[34, 86], [154, 206]], JOG = [[12, 28], [92, 108], [172, 188]], TOTE = [[62, 108], [182, 228]];
const LOOK_FERRY = [[21.5, 30], [151.5, 160]];       // heads turn at the horn
const gapLevel = t => env(t, GAP.a, GAP.b, GAP.f);
const walkF = t => Math.floor(t * 3.2) & 1;          // stride frame (3.2 a second: a whole number per loop)
const inWin = (t, list) => { for (const w of list) if (t >= w[0] && t < w[1]) return (t - w[0]) / (w[1] - w[0]); return -1; };
/** the aircraft lamps pulse together, slowly: 0..1 */
const beacon = t => smooth(0.25, 0.8, 0.5 + 0.5 * wave(t, 80));

/* ================= The crowd ================= */
// The share of the people the scene shows by itself: about half of a cast of 16. render(t, { crowd }) overrides it;
// rank 0 (the photographer) stays while anybody does; crowd 0 draws nobody, and takes the tripod, the thermos, the
// cups, the dog, the bike and the umbrella along.
const CROWD = 0.5;
const here = (rank, crowd) => crowd > 0 && (rank === 0 || rank < crowd);
const RANK = { photo: 0, bench: 0.12, dog: 0.2, coffee: 0.3, couple: 0.4, jog: 0.45, framer: 0.47, phone: 0.6, tote: 0.65, friends: 0.7, child: 0.8, umbrella: 0.9 };
const JK = { red: '#c83a34', mustard: '#d8a020', navy: '#2c4a7a', teal: '#1f8a80', purple: '#7a4a9a', yellow: '#f0d040', orange: '#e87a2a',
  forest: '#3a6a3c', beige: '#b8b0a0', green: '#3a7a5a', rust: '#c8683a', grey: '#8a8e9a', blue: '#2a6a8a', hivis: '#d8e83a', plum: '#7a3a62' };
const DARK = '#22242e', DENIM = '#2c3850', KHAKI = '#5a5444';
const CAST = {
  photo: look(22, JK.red, DARK, { skin: 0, pack: '#3a3f4c' }),
  coffee: look(21, JK.mustard, DENIM, { skin: 1 }),
  him: look(23, JK.navy, DARK, { skin: 0, hood: false, hat: '#d8d0c0' }), her: look(21, JK.teal, DENIM, { skin: 2 }),
  parent: look(22, JK.purple, DARK, { skin: 1 }), child: look(13, JK.yellow, '#3a4a7a', { skin: 1 }),
  friendA: look(21, JK.orange, KHAKI, { skin: 3, warm: true }), friendB: look(22, JK.forest, DARK, { skin: 0, hood: false, hair: '#6a4222', warm: true }),
  tourist: look(21, JK.beige, DENIM, { skin: 0, hood: false, hair: '#1c1412' }),
  benchA: look(24, JK.green, DARK, { skin: 0 }), benchB: look(24, JK.rust, DARK, { skin: 2, hood: false, hat: '#3a3a52' }),
  phone: look(24, JK.grey, DARK, { skin: 1, warm: true, dk: 0.2 }),
  framer: look(25, JK.blue, DENIM, { skin: 0, dk: 0.36 }),
  dog: look(25, JK.blue, KHAKI, { skin: 2, dk: 0.34 }), jog: look(25, JK.hivis, DARK, { skin: 0, hood: false, hat: '#22242c', strip: '#f4f8ff', dk: 0.34 }),
  tote: look(26, JK.plum, DARK, { skin: 1, dk: 0.36 }),
};

/* ================= Drizzle (3 depths), slanting on the breeze; the drops catch the lamp, the saucer and the sign ================= */
// w: 1 warm, 2 the Needle's white, 3 the sign's green
const RAIN_LIGHTS = [{ x: LAMP.x, y: LAMP.y + 6, r: 46, w: 1, near: true }, { x: ND.cx, y: 50, r: 26, w: 2 }, { x: ND.cx, y: 168, r: 22, w: 2 }]
  .map(l => ({ ...l, r2: l.r * l.r }));
const RAIN_LIGHTS_BB = [...RAIN_LIGHTS, { x: BILL.x + 23, y: BILL.y + 10, r: 27, w: 3, r2: 27 * 27 }];
const DRIZ = [
  { count: 150, nMin: 84, nMax: 104, fall: 200, len: 2, slope: 0.16, far: true, cols: ['rgba(150,170,202,0.26)', 'rgba(255,220,170,0.5)', 'rgba(226,236,255,0.5)', 'rgba(130,255,176,0.46)'], land: a => 150 + Math.floor(a * 46) },
  { count: 110, nMin: 100, nMax: 124, fall: 240, len: 3, slope: 0.2, cols: ['rgba(166,184,214,0.33)', 'rgba(255,222,172,0.6)', 'rgba(232,240,255,0.56)', 'rgba(140,255,184,0.52)'], land: a => 198 + Math.floor(a * 26) },
  { count: 52, nMin: 124, nMax: 150, fall: 300, len: 4, slope: 0.26, cols: ['rgba(190,206,232,0.44)', 'rgba(255,226,180,0.76)', 'rgba(240,244,255,0.6)', 'rgba(150,255,190,0.6)'], land: a => 226 + Math.floor(a * 50) },
];
DRIZ.forEach((L, li) => {
  const r = rng(200 + li);
  L.drops = [];
  for (let i = 0; i < L.count; i++) L.drops.push({ id: i + li * 1000, n: L.nMin + Math.floor(r() * (L.nMax - L.nMin + 1)), ph: r() });
});
function litAt(x, y, far, lights) {
  for (const l of lights) { if (far && l.near) continue; const dx = x - l.x, dy = y - l.y; if (dx * dx + dy * dy < l.r2) return l.w; }
  return 0;
}
function drawDrizzle(L, t, lights) {
  const sl = L.slope * (0.6 + 0.9 * breeze(t)), span = W + 12 + L.fall * 0.5;
  for (const d of L.drops) {
    const q = t * d.n / LOOP + d.ph, fq = Math.floor(q), cyc = fq % d.n, u = q - fq;
    const yl = L.land(hash(d.id, cyc, 12)), xl = -L.fall * 0.5 - 6 + hash(d.id, cyc, 11) * span;
    const yh = yl - L.fall * (1 - u);
    if (yh < -1) continue;
    const xh = xl + (yl - yh) * sl;
    ctx.fillStyle = L.cols[litAt(xh, yh, L.far, lights)];
    for (let k = 0; k < L.len; k++) ctx.fillRect(Math.round(xh + k * sl), Math.round(yh - k), 1, 1);
  }
}
const RINGS = [];
{ const r = rng(71); for (let i = 0; i < 18; i++) RINGS.push({ id: i, n: 70 + Math.floor(r() * 40), ph: r(), pud: i % PUDDLES.length }); }
// a picked weather (site/js/engine/weather.js): Seattle draws its own drizzle; rain, snow and leaves land on the roofs
// below the wall, on the terrace and past the bottom of the frame, and catch the same lights
const WEATHER = weatherPlans(['drizzle']);
const WX_LAND = [[150, 196], [200, FOOT + 26], [H + 4, H + 4]];
const wxLights = list => list.map(l => ({ x: l.x, y: l.y, rx: l.r, ry: l.r, col: l.w === 3 ? '#8cffb8' : l.w === 2 ? '#e8f0ff' : '#ffd6a0' }));
const WX = { land: WX_LAND, lights: wxLights(RAIN_LIGHTS) }, WX_BB = { land: WX_LAND, lights: wxLights(RAIN_LIGHTS_BB) };

/* ================= Smoke and steam puffs ================= */
const PUFFS = [];
const SMOKE = [];
{ const r = rng(61); for (let i = 0; i < 27; i++) SMOKE.push({ n: 26 + Math.floor(r() * 10), ph: r(), k: 14 + Math.floor(r() * 16), p: r(), src: i % 3 }); }
const STEAM = [];
{ const r = rng(62); for (let i = 0; i < 25; i++) STEAM.push({ n: 16 + Math.floor(r() * 8), ph: r(), k: 10 + Math.floor(r() * 12), p: r(), src: i % STEAMS.length }); }

/* ================= Pre-render everything static ================= */
let HL, SKY_C, CLOUDS, WISPS, FOGS, MT_C, VEIL_C, GAP_C, HAZE_C, FAR, DT, BAY_C, MID, NEEDLE_C, FERRY_C, SHIP_C, PLANE_C, NEAR_C, NEAR_BARE, BILL_C, POSTER_C,
  MOSS_C, MOSS_DIM, PITCH, FIRS, GROUND_C, PROPS_C, BENCH_C, VIG_C, CROW_SIT_C, CROW_FLY_C, GULL_C, DOG_C, BIKE_C, UMB_C, WHEEL_HL;
function prerender() {
  HL = {
    city: makeHalo(120, '#ff9a78', 0.07, 1.6), needle: makeHalo(40, '#ffe6b8', 0.2, 1.6), foot: makeHalo(34, '#dfe8ff', 0.1, 1.5),
    red: makeHalo(4, '#ff3a30', 0.6, 1), white: makeHalo(4, '#ffffff', 0.6, 1), green: makeHalo(4, '#40ff80', 0.5, 1),
    lift: makeHalo(3, '#ffd27a', 0.6, 1), crane: makeHalo(7, '#ffe6b0', 0.38, 1.3), win: makeHalo(6, '#ffc070', 0.2),
    porch: makeHalo(9, '#ffc070', 0.34, 1.4), slamp: makeHalo(6, '#ffb060', 0.4, 1.3), head: makeHalo(7, '#fff4d8', 0.5, 1.2),
    tail: makeHalo(4, '#ff3a30', 0.5, 1), sign: makeHalo(34, '#33ff77', 0.13, 1.6), lamp: makeHalo(58, '#ffb866', 0.26, 1.7),
    lampCore: makeHalo(15, '#ffe6b0', 0.5, 1.4), ferry: makeHalo(14, '#ffe0a0', 0.22), ship: makeHalo(9, '#ffd8a0', 0.3),
    arena: makeHalo(22, '#ffd9a0', 0.12, 1.5), arch: makeHalo(13, '#e0e8ff', 0.13, 1.4), phone: makeHalo(5, '#bfe6ff', 0.4, 1.2),
    tv: makeHalo(7, '#8ab8ff', 0.2),
  };
  WHEEL_HL = {};
  for (const c of WHEEL_COLS) if (!WHEEL_HL[c]) WHEEL_HL[c] = makeHalo(17, c, 0.2, 1.5);
  SKY_C = renderSky(); CLOUDS = CLOUD_DEFS.map(makeCloudLayer);
  WISPS = [renderWisp(91, 38, 0.85, '#807884', '#686672', 6), renderWisp(92, 34, 0.75, '#74788a', '#5c6276', 6)];
  FOGS = FOG_DEFS.map(makeFog);
  MT_C = renderRainier(); VEIL_C = renderVeil(); GAP_C = renderGap(SKY_C); HAZE_C = trim(renderHaze());
  FAR = renderFar(); FAR.L = trim(FAR.c);
  DT = renderDowntown(); DT.L = trim(DT.c);
  BAY_C = renderBay();
  MID = renderMid(); MID.L = trim(MID.c);
  NEEDLE_C = trim(renderNeedle());
  FERRY_C = renderFerry(); SHIP_C = renderShip(); PLANE_C = sprite(PLANE_ROWS, PLANE_KEY);
  NEAR_C = trim(renderNear(false)); NEAR_BARE = trim(renderNear(true)); FIRS = FIR_DEFS.map(renderFir);
  BILL_C = trim(renderBillFrame()); POSTER_C = renderPoster();
  MOSS_C = bigText('MOSS', '#4dff8c', '#d6ffe2', '#0f5a30'); MOSS_DIM = bigText('MOSS', '#1c6a3c', '#2a8a50', '#0a2a18');
  PITCH = makePitch(BILL.w, BILL.h, { accent: '#33ff77' });
  GROUND_C = trim(renderGround()); PROPS_C = trim(renderProps()); BENCH_C = renderBench(); VIG_C = renderVignette();
  CROW_SIT_C = CROW_SIT.map(rows => sprite(rows, CROW_KEY)); CROW_FLY_C = CROW_FLY.map(rows => sprite(rows, CROW_KEY));
  GULL_C = GULL_ROWS.map(rows => [sprite(rows, GULL_KEY), sprite(rows, GULL_KEY, true)]);
  DOG_C = DOG_ROWS.map(rows => [sprite(rows, DOG_KEY), sprite(rows, DOG_KEY, true)]);
  BIKE_C = sprite(BIKE_ROWS, BIKE_KEY); UMB_C = sprite(UMB_ROWS, UMB_KEY);
  for (let pr = 1; pr <= 5; pr++) {
    const s = pr * 2 + 1, p = painter(s, s);
    for (let y = 0; y < s; y++) for (let i = 0; i < s; i++) {
      const d = Math.hypot(i - pr, y - pr) / pr;
      if (d < 1 && (1 - d) * 1.6 > bayer(i, y) * 0.9) p.set(i, y, y < pr * 0.7 ? '#8a94a6' : '#646e80');
    }
    PUFFS[pr] = p.done();
  }
}

/* ================= Per-frame: sky, the mountain, aircraft ================= */
// Everything in the air drifts in from the Sound, right to left. Each layer steps a whole pixel at a time; `sp` (0..1)
// shifts when it steps, so the layers don't all tick on the same frame.
const scrollL = (t, k, sp) => W - Math.floor(frac(k * t / LOOP + sp / W) * W);
function drawScrollL(cv, y, k, t, a = 1, sp = 0) {
  const off = scrollL(t, k, sp);
  ctx.globalAlpha = a; ctx.drawImage(cv, off - W, y); ctx.drawImage(cv, off, y); ctx.globalAlpha = 1;
}
/** a mist bank over columns [x0, x1) at aMul of its strength */
function drawFog(f, t, x0 = 0, x1 = W, aMul = 1) {
  const D = clamp(f.dens(t), 0, 0.999) * FOG_LEVELS, k = Math.floor(D), fr = D - k, off = scrollL(t, f.k, f.sp);
  for (const [cv, a] of [[f.levels[k], 1 - fr], [f.levels[k + 1], fr]]) {
    if (a * f.alpha * aMul < 0.004) continue;
    ctx.globalAlpha = a * f.alpha * aMul;
    for (const o of [off - W, off]) {                                 // the two tiles, each cut to the columns asked for
      const a0 = Math.max(x0, o), a1 = Math.min(x1, o + W);
      if (a1 > a0) ctx.drawImage(cv, a0 - o, 0, a1 - a0, f.h, a0, f.y, a1 - a0, f.h);
    }
  }
  ctx.globalAlpha = 1;
}
/* an airliner far to the south, sinking toward Sea-Tac: a tail light and a slow red beacon */
function drawAirliner(t) {
  const u = inWin(t, [[AIRLINER.t0, AIRLINER.t1]]);
  if (u < 0) return;
  const x = Math.round(lerp(392, 428, u)), y = Math.round(lerp(56, 114, u)), a = smooth(0, 0.12, u) * (1 - smooth(0.82, 1, u));
  halo(HL.white, x, y, 0.75 * a);
  ctx.globalAlpha = a; px(ctx, x, y, '#fff8e8'); ctx.globalAlpha = 1;
  const b = Math.max(0, wave(t, 120, 0.1));
  ctx.globalAlpha = a * b; px(ctx, x + 1, y, '#ff4a3a'); ctx.globalAlpha = 1;
}
function drawSky(t, g) {
  ctx.drawImage(SKY_C, 0, 0);
  ctx.globalAlpha = 0.94 + 0.04 * wave(t, 5); ctx.drawImage(VEIL_C, MT.x - 38, MT.y - 14); ctx.globalAlpha = 1;
  drawScrollL(CLOUDS[0].c, CLOUDS[0].y, CLOUDS[0].k, t);
  drawScrollL(CLOUDS[1].c, CLOUDS[1].y, CLOUDS[1].k, t, 1, 0.2);
  drawAirliner(t);
  drawScrollL(CLOUDS[2].c, CLOUDS[2].y, CLOUDS[2].k, t, 1, 0.4);
  drawScrollL(CLOUDS[3].c, CLOUDS[3].y, CLOUDS[3].k, t, 1, 0.5);
  if (g > 0.01) {                                                     // the deck parts over the mountain: clear dusk, then the pale cone
    ctx.globalAlpha = g; ctx.drawImage(GAP_C, GAP_BOX.x, GAP_BOX.y);
    ctx.globalAlpha = 0.6 * g; ctx.drawImage(MT_C, MT.x, MT.y); ctx.globalAlpha = 1;
  }
  halo(HL.city, 200, 150, 0.9); halo(HL.city, 256, 160, 0.5);
}
/* a floatplane letting down toward Lake Union, beyond the Needle: high wing, floats, a landing light */
const PLANE_ROWS = ['...........t.', '..wwwwwwwww.t', '.....k..k..tt', 'ybbwwwwwwwwgg', '.bwGGwGwww...', '..s.....s....', '.ffffffffff..'];
const PLANE_KEY = { w: '#d0d6e2', b: '#c8a83a', k: '#3a404c', g: '#a0a8b8', t: '#c8a83a', G: '#2e3a4e', s: '#4a505c', f: '#aab2c0', y: '#fff6d0' };
function drawSeaplane(t) {
  const u = inWin(t, [[SEAPLANE.t0, SEAPLANE.t1]]);
  if (u < 0) return;
  const x = Math.round(lerp(496, -22, u)), y = Math.round(lerp(40, 104, Math.pow(u, 0.9)) + wave(t, 40, 0.2));
  ctx.drawImage(PLANE_C, x, y);
  halo(HL.head, x, y + 3, 0.7);
  const b = Math.max(0, wave(t, 160, 0.3));
  if (b > 0.2) { px(ctx, x + 6, y, '#ff4a3a'); halo(HL.red, x + 6, y, b * 0.8); }
}
function drawFar(t) {
  blit(FAR.L);
  const st = step(t, 240), b = beacon(t);
  for (const l of FAR.twinkle) if (hash(l.id, st, 6) < 0.7) px(ctx, l.x, l.y, l.col);
  for (const m of MASTS) for (const dy of [-1, 14]) { px(ctx, m.x, m.top + dy, b > 0.5 ? '#ff4a3a' : '#5a1c1c'); halo(HL.red, m.x, m.top + dy, b * 0.7); }
  for (const c of CRANES) halo(HL.crane, c.x, CRANE_TOP(c) - 1, 0.8 + 0.1 * wave(t, 300 + c.x, 0.1));
}
function drawDowntown(t) {
  blit(DT.L);
  for (const w of DT.wins) if (winOn(w, 0, t)) px(ctx, w.x, w.y, w.col);
  const b = beacon(t);
  for (const [bx, by] of BEACONS) { px(ctx, bx, by, b > 0.5 ? '#ff4a3a' : '#6a2020'); halo(HL.red, bx, by, b * 0.7); }
  const bz = breeze(t);
  for (const s of STEAM) {                                            // steam off the rooftops, torn away by the breeze
    const q = t * s.n / LOOP + s.ph, u = q - Math.floor(q), [sx, sy] = STEAMS[s.src];
    const x = sx - u * (6 + 14 * bz) + wave(t, s.k, s.p) * 1.5 * u, y = sy - 1 - u * 15 + u * u * 4, rr = Math.min(3, 1 + Math.floor(u * 3));
    ctx.globalAlpha = 0.3 * Math.min(1, u * 6) * (1 - u);
    ctx.drawImage(PUFFS[rr], Math.round(x) - rr, Math.round(y) - rr);
  }
  ctx.globalAlpha = 1;
  blit(HAZE_C, 0.3);
  drawScrollL(WISPS[0], 50, 2, t, 0.85, 0.45);                              // low cloud through the tops of the towers
  drawScrollL(WISPS[1], 78, 3, t, 0.8, 0.85);
}

/* ================= Per-frame: Elliott Bay, the buoy, the ferry, the ship at anchor ================= */
const BAY_STREAKS = [...CRANES.map((c, i) => ({ x: c.x, w: 2, col: '#ffe0a8', a: 0.34, seed: i + 1 })),
  ...[392, 458, 470].map((x, i) => ({ x, w: 2, col: '#f2c078', a: 0.2, seed: 20 + i }))];
function drawFerry(t) {
  for (const f of FERRY) {
    if (t < f.t0 || t >= f.t1) continue;
    const u = (t - f.t0) / (f.t1 - f.t0), v = f.dir > 0 ? u : 1 - u;
    const x = Math.round(lerp(FERRY_PATH.x0, FERRY_PATH.x1, v)), wl = Math.round(lerp(FERRY_PATH.y0, FERRY_PATH.y1, v));
    const bx = x - (FERRY_W >> 1), by = wl - FERRY_H + 1;
    for (let j = 0; j < 7; j++) {                                     // her lights on the water
      const off = Math.round(Math.sin(TAU * 45 * t / LOOP + j * 0.7) * (0.5 + j * 0.15));
      ctx.globalAlpha = 0.34 * (1 - j / 7);
      ctx.drawImage(FERRY_C, 0, Math.max(0, FERRY_H - 3 - j), FERRY_W, 1, bx + off, wl + 1 + j, FERRY_W, 1);
    }
    ctx.globalAlpha = 1;
    for (let k = 0; k < 16; k++) {                                    // the wake astern
      const wx = x - f.dir * ((FERRY_W >> 1) + 1 + k * 2 + (k * k >> 4)), wy = wl - (k % 3 === 0 ? 1 : 0);
      if (hash(k, step(t, 1440), 17) < 0.7 - k * 0.035) px(ctx, wx, wy, k < 5 ? '#d0dae2' : '#7e929e');
    }
    ctx.drawImage(FERRY_C, bx, by);
    streak(ctx, t, x - 11, 5, '#ffdc98', 0.3, 7, wl + 1, wl + 13); streak(ctx, t, x + 11, 5, '#ffdc98', 0.3, 8, wl + 1, wl + 13);
    halo(HL.ferry, x, by + 5, 0.9);
    const sx = x + f.dir * 6;                                         // her side light: green going out (starboard to us), red coming in
    px(ctx, sx, by + 2, f.dir > 0 ? '#40ff80' : '#ff4a3a'); halo(f.dir > 0 ? HL.green : HL.red, sx, by + 2, 0.6);
    px(ctx, x, by - 1, '#ffffff'); halo(HL.white, x, by - 1, 0.6);
  }
}
function drawBay(t) {
  ctx.drawImage(BAY_C, BAY_X, SHORE);
  reflect(ctx, t, { top: SHORE, rows: 24, squash: 1.2, alpha: 0.44, amp: 0.8, grow: 0.06, x: BAY_X, w: W - BAY_X });
  for (let y = SHORE + 2, n = 0; y < WALL; y += 2, n++) for (let j = 0; j < 3; j++) {   // drifting ripple troughs and glints
    const k = 2 + ((n * 7 + j * 5) % 7), len = 4 + Math.floor(hash(n, j, 41) * 12);
    const x = BAY_X + Math.round(frac(hash(n, j, 40) - k * t / LOOP) * (W - BAY_X + 30)) - 15;
    if (x < BAY_X) continue;
    ctx.globalAlpha = j === 2 ? 0.3 : 0.42;
    rect(ctx, x, y + (j & 1), len, 1, j === 2 ? '#7a98a4' : '#070d13');
  }
  ctx.globalAlpha = 1;
  for (const s of BAY_STREAKS) streak(ctx, t, s.x, s.w, s.col, s.a, s.seed, SHORE + 1, SHORE + 22);
  drawFerry(t);
  // the bell buoy: a slow green flash, rocking on the swell
  const bob = Math.round(wave(t, 30, 0.2)), gl = Math.pow(Math.max(0, wave(t, 60)), 2), bx = BUOY.x, by = BUOY.y + bob;
  R(bx - 1, by - 3, 3, 3, '#a83a30'); R(bx - 1, by - 3, 3, 1, '#d8584a'); R(bx, by - 6, 1, 3, '#3a3f4a');
  px(ctx, bx, by - 7, gl > 0.3 ? '#8cffb0' : '#1e5a34'); halo(HL.green, bx, by - 7, gl);
  streak(ctx, t, bx, 2, '#40ff80', 0.3 * gl, 5, by + 1, by + 8);
  for (const tb of BELLS) {                                           // the swell that rings it
    const dt = t - tb;
    if (dt < 0 || dt > 3) continue;
    const rr = 2 + dt * 3;
    ctx.globalAlpha = 0.5 * (1 - dt / 3); ctx.fillStyle = '#9ab4c0';
    for (let j = 0; j < 10; j++) { const a = j / 10 * TAU; ctx.fillRect(Math.round(bx + Math.cos(a) * rr), Math.round(BUOY.y + Math.sin(a) * rr * 0.35), 1, 1); }
    ctx.globalAlpha = 1;
  }
  // the bulk carrier swinging at her anchor
  const sx = SHIP.x + Math.round(wave(t, 2, 0.3) * 1.5), sy = SHIP.y - SHIP_H + 1;
  for (let j = 0; j < 6; j++) {
    const off = Math.round(Math.sin(TAU * 45 * t / LOOP + j * 0.8) * (0.5 + j * 0.15));
    ctx.globalAlpha = 0.4 * (1 - j / 6); ctx.drawImage(SHIP_C, 0, SHIP_H - 1 - j, SHIP_W, 1, sx + off, SHIP.y + 1 + j, SHIP_W, 1);
  }
  ctx.globalAlpha = 1;
  ctx.drawImage(SHIP_C, sx, sy);
  px(ctx, sx + 3, sy + 5, '#ffffff'); halo(HL.white, sx + 3, sy + 5, 0.8); px(ctx, sx + 49, sy - 1, '#ffffff'); halo(HL.white, sx + 49, sy - 1, 0.7);
  halo(HL.ship, sx + 48, sy + 5, 0.9); halo(HL.ship, sx + 21, sy + 4, 0.4);
  streak(ctx, t, sx + 48, 4, '#ffd48a', 0.3, 31, SHIP.y + 1, SHIP.y + 12); streak(ctx, t, sx + 3, 2, '#f4f8ff', 0.26, 32, SHIP.y + 1, SHIP.y + 10);
}

/* ================= Per-frame: Belltown and Seattle Center, the Great Wheel, the Needle ================= */
function drawWheel(t) {
  const { cx, cy, r } = WHEEL, q = t / 30, i = Math.floor(q) % WHEEL_COLS.length, f = smooth(0.86, 1, q - Math.floor(q));
  const ca = WHEEL_COLS[i], cb = WHEEL_COLS[(i + 1) % WHEEL_COLS.length];
  halo(WHEEL_HL[ca], cx, cy, 0.9 * (1 - f)); halo(WHEEL_HL[cb], cx, cy, 0.9 * f);
  const col = mixHex(ca, cb, f), dim = mixHex(col, '#141a26', 0.6), rot = TAU * 6 * t / LOOP;   // a turn every 40 s
  ctx.fillStyle = dim;
  for (let s = 0; s < 6; s++) { const a = rot + s * TAU / 6; for (let k = 2; k < r; k += 2) ctx.fillRect(Math.round(cx + Math.sin(a) * k), Math.round(cy - Math.cos(a) * k), 1, 1); }
  for (const p of WHEEL_RIM) { ctx.fillStyle = Math.sin(3 * (p.a - rot)) > 0.2 ? col : dim; ctx.fillRect(p.x, p.y, 1, 1); }
  for (let k = 0; k < 8; k++) { const a = rot + k * TAU / 8; px(ctx, Math.round(cx + Math.sin(a) * r), Math.round(cy - Math.cos(a) * r), '#ffffff'); }   // its gondolas
  px(ctx, cx, cy, '#ffffff');
}
function drawMid(t) {
  blit(MID.L);
  for (const w of MID.wins) if (winOn(w, 500, t)) px(ctx, w.x, w.y, w.col);
  halo(HL.arena, 245, 184, 0.9);
  for (const ax of ARCHES) halo(HL.arch, ax, 176, 0.5);
  drawWheel(t);
}
const LIFTS = [{ k: 6, ph: 0, dx: -1 }, { k: 4, ph: 0.37, dx: 1 }];   // a trip up and down every 40 s and every 60 s
function drawNeedle(t) {
  halo(HL.needle, ND.cx, 50, 0.9 + 0.06 * wave(t, 6)); halo(HL.foot, ND.cx, 176, 0.9);
  blit(NEEDLE_C);
  const rot = TAU * 4 * t / LOOP;                                     // the halo's lights go round once a minute
  for (let i = 0; i < 24; i++) {
    const a = i * TAU / 24 + rot;
    if (Math.cos(a) < 0.05) continue;
    px(ctx, ND.cx + Math.round(Math.sin(a) * HALO_HW), HALO_Y, i % 4 === 0 ? '#ffffff' : '#ffe2a0');
  }
  for (const L of LIFTS) {                                            // the lifts' lights climbing the core
    const v = 0.5 - 0.5 * Math.cos(TAU * (L.k * t / LOOP + L.ph)), y = Math.round(lerp(190, 60, v));
    R(ND.cx + L.dx, y, 1, 2, '#ffd27a'); px(ctx, ND.cx + L.dx, y, '#fff6dc');
    halo(HL.lift, ND.cx + L.dx, y, 0.8);
  }
  const b = beacon(t);
  px(ctx, ND.cx, ND.tip, b > 0.5 ? '#ff5040' : '#7a2420'); halo(HL.red, ND.cx, ND.tip, 0.3 + 0.7 * b);
}
/* the evening crow flight: loose strings of crows heading for their roost, north-east over the hill */
const CROWS = [];
{ const r = rng(515); for (let i = 0; i < 15; i++) CROWS.push({ d: r() * 0.34, y: 70 + r() * 54, k: 20 + Math.floor(r() * 30), p: r(), rise: 6 + r() * 14, sp: 0.85 + r() * 0.3 }); }
function drawCrows(t) {
  CROWS_T.forEach(([a, b], pass) => {
    if (t < a || t >= b) return;
    const tw = (t - a) / (b - a);
    CROWS.forEach((c, i) => {
      const u = (tw - c.d) / 0.62 * c.sp;
      if (u <= 0 || u >= 1) return;
      const x = Math.round(lerp(492, -12, u)), y = Math.round(c.y + (pass ? 10 : 0) - c.rise * u + 3 * wave(t, c.k, c.p));
      ctx.drawImage(CROW_FLY_C[Math.floor(t * 5 + i * 0.37) % 3], x - 3, y - 1);
    });
  });
}
function drawGulls(t) {
  for (const g of GULLS_T) {
    if (t < g.a || t >= g.b) continue;
    const u = (t - g.a) / (g.b - g.a), x = Math.round(g.dir > 0 ? lerp(-18, W + 4, u) : lerp(W + 4, -18, u)), y = Math.round(g.y0 + g.dip * Math.sin(Math.PI * u));
    const glide = u > 0.3 && u < 0.7, spr = GULL_C[glide ? 1 : Math.floor(t * 4) % 3][g.dir > 0 ? 0 : 1];
    ctx.drawImage(spr, x, y - (spr.height >> 1));
  }
}

/* ================= Per-frame: the hill below the park ================= */
/** a car on the steep street; u: 0 at the far end .. 1 at the near end, up: climbing toward us (headlights) */
function drawCar(u, up, bus) {
  const y = Math.round(STREET.y0 + (STREET.y1 + 3 - STREET.y0) * Math.pow(u, 1.7)), s = streetAt(y);
  const w = Math.max(2, Math.round(s.hw * (bus ? 0.82 : 0.66))), h = Math.max(2, Math.round(w * (bus ? 1.15 : 0.7)));
  const x = Math.round(s.cx + (up ? -0.48 : 0.48) * s.hw - w / 2), top = y - h, a = (0.4 + 0.6 * u) * smooth(0, 0.08, u);
  ctx.globalAlpha = smooth(0, 0.08, u);
  R(x, top, w, h, bus ? '#1c5a62' : '#141820'); R(x, top, w, 1, bus ? '#3a8a90' : '#39424f');
  if (bus) {                                                          // a trolleybus: lit windows, a yellow band, its two poles on the wires
    R(x, top + Math.max(1, h >> 2), w, Math.max(1, h >> 2), '#ffe2a0'); R(x, top + (h >> 1) + 1, w, 1, '#d8b040');
    if (h > 5) { R(x + (w >> 1) - 1, top - 3, 1, 3, '#2a303a'); R(x + (w >> 1) + 1, top - 3, 1, 3, '#2a303a'); }
  } else if (h > 3) R(x + 1, top + 1, w - 2, 1, '#2a3444');
  ctx.globalAlpha = 1;
  if (up) {
    px(ctx, x, y - 1, '#fff6dc'); px(ctx, x + w - 1, y - 1, '#fff6dc'); halo(HL.head, x + (w >> 1), y - 1, a);
    ctx.globalAlpha = 0.35 * a; R(x, y, w, 2 + Math.round(u * 3), '#ffe6b0'); ctx.globalAlpha = 1;      // on the wet road
  } else {
    px(ctx, x, y - 1, '#ff4030'); px(ctx, x + w - 1, y - 1, '#ff4030'); halo(HL.tail, x + (w >> 1), y - 1, a);
    ctx.globalAlpha = 0.3 * a; R(x, y, w, 2, '#ff5040'); ctx.globalAlpha = 1;
  }
}
function drawTraffic(t) {
  let tau = (t - CAR_UP.from + LOOP) % CAR_UP.every;
  if (tau < CAR_UP.dur) drawCar(tau / CAR_UP.dur, true, false);
  tau = (t - CAR_DOWN.from + LOOP) % CAR_DOWN.every;
  if (tau < CAR_DOWN.dur) drawCar(1 - tau / CAR_DOWN.dur, false, false);
  for (const b of TROLLEY) if (t >= b.t0 && t < b.t1) { const u = (t - b.t0) / (b.t1 - b.t0); drawCar(b.up ? u : 1 - u, b.up, true); }
}
function drawSmoke(t) {
  const bz = breeze(t);
  for (const s of SMOKE) {
    const q = t * s.n / LOOP + s.ph, u = q - Math.floor(q), [sx, sy] = CHIMNEYS[s.src];
    const x = sx - u * (10 + 26 * bz) + wave(t, s.k, s.p) * 2 * u, y = sy - 2 - u * 30 + u * u * 8, rr = Math.min(5, 1 + Math.floor(u * 5));
    ctx.globalAlpha = 0.34 * Math.min(1, u * 7) * Math.pow(1 - u, 1.3);
    ctx.drawImage(PUFFS[rr], Math.round(x) - rr, Math.round(y) - rr);
  }
  ctx.globalAlpha = 1;
}
const TV_COLS = ['#6a9adc', '#8ab8f0', '#5a7ab8', '#a8c8f8'];
function drawSlope(t, billboard) {
  blit(billboard ? NEAR_C : NEAR_BARE);
  const bz = 0.6 + 0.8 * breeze(t);
  for (const f of FIRS) {                                             // the firs sway from the foot up, the tips most
    const { c, x, y } = f.L, h = c.height, sw = (bz * wave(t, f.k, f.ph) + 0.4 * wave(t, f.k * 3 + 1, f.ph)) * 1.7;
    for (let j = 0; j < h;) {
      const off = Math.round(sw * Math.pow(1 - j / h, 1.4));
      let j1 = j + 1;
      while (j1 < h && Math.round(sw * Math.pow(1 - j1 / h, 1.4)) === off) j1++;
      ctx.drawImage(c, 0, j, c.width, j1 - j, x + off, y + j, c.width, j1 - j);
      j = j1;
    }
  }
  for (const w of NEAR_WINS) {
    if (!winOn(w, 900, t)) continue;
    if (w.tv) { R(w.x, w.y, w.w, w.h, TV_COLS[Math.floor(hash(w.id, step(t, 480), 9) * 4)]); halo(HL.tv, w.x + 2, w.y + 3, 0.7); }
    else { R(w.x, w.y, w.w, w.h, '#f0b060'); R(w.x, w.y, w.w, w.h >> 1, '#ffe0a0'); R(w.x + (w.w >> 1), w.y, 1, w.h, '#a8763e'); halo(HL.win, w.x + 2, w.y + 3, 0.7); }
  }
  for (const [lx, ly] of PORCH) halo(HL.porch, lx, ly, 0.85 + 0.1 * wave(t, 40 + lx, 0.3));
  for (const l of STREET_LAMPS) halo(HL.slamp, l.x, l.y, 0.5 + 0.4 * l.u);
  drawTraffic(t);
  drawSmoke(t);
}
/* the rooftop sign: its glow in the drizzle, the frame, the face (the city's own ad or the pitch) */
function drawBillboard(t, pitch) {
  halo(HL.sign, BILL.x + 23, BILL.y + 9, 0.85 + 0.1 * wave(t, 7, 0.2));
  blit(BILL_C);
  if (!PITCH.covers(t, pitch)) {
    ctx.drawImage(POSTER_C, BILL.x, BILL.y);
    const a = TAU * 120 * t / LOOP, cx = BILL.x + DISC_C.x, cy = BILL.y + DISC_C.y;                    // the record turns
    for (const o of [0, Math.PI]) {
      px(ctx, cx + Math.round(Math.cos(a + o) * 4.4), cy + Math.round(Math.sin(a + o) * 4.4), '#8a94a8');
      px(ctx, cx + Math.round(Math.cos(a + o + 0.3) * 3.5), cy + Math.round(Math.sin(a + o + 0.3) * 3.5), '#4a5262');
    }
    px(ctx, cx + Math.round(Math.cos(a) * 1.8), cy + Math.round(Math.sin(a) * 1.8), '#fff0d0');
    ctx.drawImage(MOSS_C, BILL.x + 15, BILL.y + 1);
    const lv = flick(t, SIGN_FLICK, 13);                                                               // the M's tube stutters
    if (lv < 1) { ctx.globalAlpha = 1 - lv; R(BILL.x + 15, BILL.y + 1, 7, 11, '#0b1a14'); ctx.drawImage(MOSS_DIM, 0, 0, 7, 11, BILL.x + 15, BILL.y + 1, 7, 11); ctx.globalAlpha = 1; }
  }
  PITCH.draw(ctx, t, BILL.x, BILL.y, pitch);
}

/* ================= Per-frame: the terrace and its people ================= */
const FEET = FOOT + 3;                                // where the people at the wall stand
function steam(x, y, t, n, id) {
  for (let k = 0; k < n; k++) {
    const { u } = cycle(t, 60 + k * 12, k * 0.37 + id * 0.11);
    ctx.globalAlpha = 0.55 * Math.sin(Math.PI * u);
    R(Math.round(x + Math.sin(u * 5 + k) * 1.2), Math.round(y - u * 6), 1, 1, '#c8d2e0');
  }
  ctx.globalAlpha = 1;
}
const cup = (x, y) => { R(x, y, 2, 3, '#ece6da'); R(x, y, 2, 1, '#3a2a22'); };
const phoneBack = (x, y) => { R(x, y, 2, 3, '#0e1018'); R(x, y, 2, 1, '#ffe2a8'); R(x, y + 1, 2, 1, '#62d8c8'); };   // its lit screen, seen from behind
const mirror = (x, fy, t) => reflect(ctx, t, { top: fy + 1, rows: 7, x: Math.round(x) - 9, w: 19, squash: 1.5, alpha: 0.24, k1: 40, k2: 90 });
function drawFarRow(t, crowd) {
  const lookR = inWin(t, LOOK_FERRY) >= 0, g = gapLevel(t);
  if (here(RANK.friends, crowd)) {                                    // two friends by a bike propped on the wall, talking
    ctx.drawImage(BIKE_C, 7, FEET - 11);
    const who = hash(step(t, 48), 1) < 0.5, wave1 = hash(step(t, 96), 2) < 0.4;
    person(35, FEET, CAST.friendA, { view: 2, dir: 1, aR: who && wave1 ? 3 : 0 });
    person(45, FEET, CAST.friendB, { view: 2, dir: -1, aR: !who && wave1 ? 2 : 0 });
  }
  if (here(RANK.coffee, crowd)) {                                     // a paper cup of coffee on the wall's cap, a sip now and then
    const sip = frac(t / 24 + 0.3) < 0.14;
    person(126, FEET, CAST.coffee, sip ? { view: 2, dir: 1, aR: 2 } : { view: 0 });
    if (sip) cup(129, PH.ty - 2); else { cup(133, WALL - 3); steam(133, WALL - 4, t, 3, 1); }
  }
  if (here(RANK.child, crowd)) {                                      // a child shows a parent the lifts going up
    person(182, FEET, CAST.parent, { view: 0 });
    person(190, FEET, CAST.child, { view: 0, aR: frac(t / 30 + 0.55) < 0.22 ? 1 : 0 });
  }
  if (here(RANK.photo, crowd)) {                                      // the photographer and a tripod: long exposures of the skyline
    const at = frac(t / 30 + 0.1) < 0.36 || g > 0.5, ty = FEET - 15;
    ctx.fillStyle = '#5a6274';
    for (let k = 0; k <= 14; k++) { ctx.fillRect(Math.round(237 - k * 0.3), ty + 1 + k, 1, 1); ctx.fillRect(Math.round(238 + k * 0.3), ty + 1 + k, 1, 1); ctx.fillRect(237, ty + 1 + Math.min(k, 9), 1, 1); }
    R(235, ty - 3, 5, 3, '#1c2028'); R(236, ty - 4, 3, 1, '#2c323e'); R(237, ty - 2, 1, 1, '#5a6478');
    if (at && (step(t, 240) & 1)) px(ctx, 235, ty - 2, '#ff4a3a');                                     // the shutter is open
    person(at ? 230 : 226, FEET, CAST.photo, at ? { view: 2, dir: 1, aR: 3, lean: 1 } : { view: 0 });
  }
  if (here(RANK.couple, crowd)) {                                     // a couple at the rail; she points out the ferry, then the mountain
    const u = frac(t / 40 + 0.2), close = u > 0.6 && u < 0.86, pointing = lookR || (g > 0.3 && frac(t / 12) < 0.4);
    person(262, FEET, CAST.him, { view: 0, aR: u < 0.5 ? 3 : 0, lean: close ? 1 : 0 });
    person(270, FEET, CAST.her, { view: 0, aR: pointing ? 3 : 0, lean: close ? -1 : 0 });
  }
  if (here(RANK.umbrella, crowd)) {                                   // the one visitor with an umbrella, taking pictures
    const snap = frac(t / 20 + 0.7) < 0.3;
    person(292, FEET, CAST.tourist, { view: 0, aR: snap ? 2 : 0 });
    if (snap) phoneBack(PH.tx + PH.bw, PH.hy - 1);
    R(289, PH.hy - 3, 1, 9, '#1a1c22');
    ctx.drawImage(UMB_C, 285, PH.hy - 7);
  }
}
function drawCrow(t) {                                                // a crow on the railing: it looks about and shifts along now and then
  const u = frac(t / 30), p = smooth(0.46, 0.5, u) - smooth(0.96, 1, u);
  const x = 352 + Math.round(9 * p), hop = p > 0.02 && p < 0.98 ? Math.round(3 * Math.sin(Math.PI * p)) : 0;
  ctx.drawImage(CROW_SIT_C[hash(3, step(t, 96), 5) < 0.5 ? 0 : 1], x, RAIL - 5 - hop);
}
function drawPuddles(t, rain) {
  PUDDLES.forEach(([cx, cy, rx, ry], pi) => {                         // the breeze shivers the standing water
    ctx.fillStyle = Math.hypot(cx - LAMP.x, (cy - 244) * 3) < 90 ? '#c8a070' : '#6f8298';
    for (let k = 0; k < 5; k++) {
      const { cyc, u } = cycle(t, 40 + ((pi * 7 + k * 13) % 30), k * 0.21 + pi * 0.13);
      const x = Math.round(cx + (hash(pi, k, cyc) * 1.6 - 0.8) * rx), y = Math.round(cy + (hash(pi + 20, k, cyc) * 1.2 - 0.6) * ry);
      ctx.globalAlpha = Math.sin(Math.PI * u) * 0.8;
      ctx.fillRect(x - 1, y, 3, 1);
    }
  });
  if (rain) {
    ctx.fillStyle = '#8ea0b8';
    for (const g of RINGS) {
      const { cyc, u } = cycle(t, g.n, g.ph);
      if (u > 0.5) continue;
      const s = u / 0.5, [cx, cy, rx, ry] = PUDDLES[g.pud];
      const x = cx + (hash(g.id, cyc, 1) - 0.5) * 1.4 * rx, y = cy + (hash(g.id, cyc, 2) - 0.5) * ry, rr = 1 + s * 2.6;
      ctx.globalAlpha = (1 - s) * 0.55;
      for (let j = 0; j < 8; j++) { const a = j / 8 * TAU; ctx.fillRect(Math.round(x + Math.cos(a) * rr), Math.round(y + Math.sin(a) * rr * 0.36), 1, 1); }
    }
  }
  ctx.globalAlpha = 1;
}
function drawMidRow(t, crowd) {
  const lookR = inWin(t, LOOK_FERRY) >= 0, g = gapLevel(t), sy = BENCH.seat - 2;
  if (here(RANK.bench, crowd)) {                                      // two on the bench share a thermos
    const u = frac(t / 40 + 0.6), pour = u < 0.12, drink = u > 0.14 && u < 0.34, close = u > 0.5 && u < 0.9;
    person(319, sy, CAST.benchA, { sit: true, aR: pour ? 3 : 0, lean: close ? 1 : 0 });
    if (pour) { R(PH.tx + PH.bw + 3, PH.ty - 2, 2, 4, '#c8ccd4'); R(PH.tx + PH.bw + 3, PH.ty - 2, 2, 1, '#c83a34'); }
    person(331, sy, CAST.benchB, { sit: true, aL: pour ? 3 : 0, aR: drink ? 2 : 0, lean: close ? -1 : 0 });
    if (drink) { R(PH.tx + PH.bw - 1, PH.hy + 2, 2, 2, '#c83a34'); steam(PH.tx + PH.bw, PH.hy + 1, t, 3, 2); }
    else if (u < 0.5) steam(325, PH.ty, t, 3, 3);
  }
  ctx.drawImage(BENCH_C, BENCH.x0, BENCH.seat - 10);
  if (here(RANK.phone, crowd)) {                                      // someone under the lamp with a phone; the horn makes them look up
    if (lookR || g > 0.6) person(78, 241, CAST.phone, { view: 0 });
    else { person(78, 241, CAST.phone, { view: 2, dir: 1, aR: 2 }); R(81, PH.ty - 2, 1, 3, '#cfe8ff'); halo(HL.phone, 81, PH.ty - 1, 0.7 + 0.2 * wave(t, 37)); }
    mirror(78, 241, t);
  }
  if (here(RANK.framer, crowd)) {                                     // framing the Needle in the sculpture's ring, as everybody does
    const shoot = frac(t / 36 + 0.4) < 0.6;
    person(446, 250, CAST.framer, { view: 2, dir: -1, aR: shoot ? 2 : 0 });
    if (shoot) { R(441, PH.ty - 3, 1, 3, '#0e1018'); px(ctx, 442, PH.ty - 2, '#9ad8ff'); }
    mirror(446, 250, t);
  }
}
function drawWalkers(t, crowd) {
  let u;
  if (here(RANK.dog, crowd) && (u = inWin(t, DOGWALK)) >= 0) {        // someone walks a dog along the terrace
    const x = lerp(W + 26, -36, u), st = walkF(t), dx = Math.round(x) - 17;
    person(x, 249, CAST.dog, { view: 2, dir: -1, step: st, aR: 3 });
    ctx.drawImage(DOG_C[st][1], dx - 5, 244);
    ctx.fillStyle = '#c83a34';
    for (let k = 1; k < 9; k++) ctx.fillRect(Math.round(x - 5 - k), Math.round(PH.ty + 2 + k * 0.55), 1, 1);
    mirror(x, 249, t); mirror(dx, 249, t);
  }
  if (here(RANK.tote, crowd) && (u = inWin(t, TOTE)) >= 0) {          // home from the shop with a tote bag
    const x = lerp(-26, W + 26, u), st = walkF(t);
    person(x, 253, CAST.tote, { view: 2, dir: 1, step: st });
    R(Math.round(x) - 4, PH.ty + 6, 3, 4, '#d8c8a0'); R(Math.round(x) - 3, PH.ty + 4, 1, 2, '#8a7a5a');
    mirror(x, 253, t);
  }
  if (here(RANK.jog, crowd) && (u = inWin(t, JOG)) >= 0) {            // a jogger with a headlamp
    const x = lerp(-20, W + 20, u), st = Math.floor(t * 6.4) & 1, fy = 259 - st;
    person(x, fy, CAST.jog, { view: 2, dir: 1, step: st, aR: 2 });
    px(ctx, Math.round(x) + 2, PH.hy + 1, '#ffffff'); halo(HL.white, Math.round(x) + 3, PH.hy + 1, 0.9);
    mirror(x, 259, t);
  }
}
const TERRACE_STREAKS = [{ x: ND.cx, w: 4, col: '#f4ecd4', a: 0.2, seed: 41 }, { x: BILL.x + 23, w: 5, col: '#46e884', a: 0.2, seed: 42, bb: true },
  { x: 245, w: 6, col: '#ffd9a0', a: 0.16, seed: 43 }, { x: 314, w: 3, col: '#ffcf88', a: 0.14, seed: 44 }, { x: 380, w: 3, col: '#ffcf88', a: 0.14, seed: 45 },
  { x: 420, w: 4, col: '#f49246', a: 0.12, seed: 46 }, { x: 30, w: 3, col: '#ffcf88', a: 0.14, seed: 47 }];
function drawTerrace(t, wx, crowd, billboard) {
  blit(GROUND_C);
  if (crowd > 0) drawFarRow(t, crowd);
  drawCrow(t);
  reflect(ctx, t, { top: FEET + 1, rows: 34, squash: 1.2, alpha: 0.3, k1: 40, k2: 90, amp: 0.8, grow: 0.05 });   // the wet stone mirrors the wall and the city's lights
  drawPuddles(t, wx.drizzle);
  const li = (0.95 + 0.05 * wave(t, 1500, 0.3)) * flick(t, LAMP_FLICK, 11);
  streak(ctx, t, LAMP.x, 5, '#ffc878', 0.36 * li, 21, LAMP.base + 1, H);
  for (const s of TERRACE_STREAKS) if (!s.bb || billboard) streak(ctx, t, s.x, s.w, s.col, s.a, s.seed, FEET + 2, H);   // far lights, drawn out down the wet stone
  blit(PROPS_C);
  drawMidRow(t, crowd);
  drawWalkers(t, crowd);
  if (li < 0.9) R(LAMP.x - 3, LAMP.y - 5, 7, 9, `rgba(24,14,8,${(0.9 - li).toFixed(3)})`);
  halo(HL.lamp, LAMP.x, LAMP.y, li); halo(HL.lampCore, LAMP.x, LAMP.y, li);
}

/* ================= Frame compositor (back to front) ================= */
// pitch: the "your ad here" face (promo captures) on the rooftop sign; billboard: false leaves the sign out (the loop
// videos); weather: a WEATHERS pick (site/js/engine/weather.js), the city's own drizzle when left out; crowd: the share
// of the people to show, 0..1 (CROWD when left out or not a number), 0 = nobody at all
function renderFrame(t, { pitch, billboard = true, weather, reducedMotion = false, crowd: share } = {}) {
  t = ((t % LOOP) + LOOP) % LOOP;
  ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.globalAlpha = 1; ctx.globalCompositeOperation = 'source-over';
  const wx = WEATHER(weather), lights = billboard ? RAIN_LIGHTS_BB : RAIN_LIGHTS, wxc = billboard ? WX_BB : WX;
  const crowd = clamp(typeof share === 'number' && share === share ? share : CROWD, 0, 1);
  drawSky(t, gapLevel(t));
  drawFar(t);
  drawDowntown(t);
  if (wx.drizzle) drawDrizzle(DRIZ[0], t, lights);
  drawWeather(ctx, wx.shared, 0, t, wxc, reducedMotion);
  drawBay(t);
  drawFog(FOGS[0], t, 0, 300); drawFog(FOGS[0], t, 300, W, 0.45);   // thick at the foot of downtown, thin over the bay
  drawMid(t);
  drawSeaplane(t);
  drawNeedle(t);
  drawCrows(t);
  if (billboard) drawBillboard(t, pitch);
  drawSlope(t, billboard);
  drawFog(FOGS[1], t);
  if (wx.drizzle) drawDrizzle(DRIZ[1], t, lights);
  drawWeather(ctx, wx.shared, 1, t, wxc, reducedMotion);
  drawGulls(t);
  drawTerrace(t, wx, crowd, billboard);
  if (wx.drizzle) drawDrizzle(DRIZ[2], t, lights);
  drawWeather(ctx, wx.shared, 2, t, wxc, reducedMotion);
  ctx.drawImage(VIG_C, 0, 0);
}

export default {
  id: 'seattle',
  name: 'Seattle',
  tagline: 'Drizzle over the Space Needle',
  timeZone: 'America/Los_Angeles',
  accent: '#33ff77',                                   // the Emerald City
  focusX: 168,
  landmark: [147, 189],                                // the Space Needle, halo to halo
  landmarkTop: 22,                                     // the row of the beacon on its spire
  billboard: BILL,
  crowd: CROWD,                                        // the share of the people shown by default; render(t, { crowd }) thins them, 0 = nobody
  ambience: { rain: 0.4, wind: 0.2, water: 0.2, traffic: 0.2 },
  events: [
    { t: HORNS[0], type: 'horn', x: 300, gain: 0.5 },
    { t: 22, type: 'boat', dur: 68, pan: [0.2, 1], gain: 0.3 },
    ...BELLS.map(t => ({ t, type: 'bell', x: BUOY.x, gain: 0.22 })),
    { t: 100, type: 'gull', x: 180, gain: 0.4 },
    { t: HORNS[1], type: 'horn', x: 470, gain: 0.45 },
    { t: 152, type: 'boat', dur: 68, pan: [1, 0.2], gain: 0.3 },
    { t: 198, type: 'gull', x: 320, gain: 0.4 },
  ],
  create(c) {
    ctx = c;
    prerender();
    return { render: renderFrame };
  },
};
